import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Grid,
  Collapse,
  IconButton,
  Alert,
  CircularProgress,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Badge,
  Tooltip,
  Snackbar,
  Tab,
  Tabs,
  useTheme
} from '@mui/material';
import {
  Verified,
  Public,
  Timeline,
  AccountBalance,
  GetApp,
  PictureAsPdf,
  TableChart,
  Wifi,
  WifiOff,
  RadioButtonChecked,
  RadioButtonUnchecked,
  Notifications,
  TrendingUp,
  Analytics,
  BarChart,
  Search,
  SearchOff,
  History,
  BookmarkBorder,
  BookmarkBorderOutlined,
  Bookmark,
  Close,
  Add,
  Remove,
  Clear,
  SavedSearch,
  FilterAlt,
  FilterAltOff,
  SwapVert,
  UnfoldMore,
  KeyboardArrowDown,
  PieChart,
  ShowChart,
  CalendarToday,
  MapOutlined,
  Insights,
  TableView,
  Assessment,
  DateRange,
  DeviceHub,
  Business,
  Park,
  Sync,
  Refresh,
  Visibility,
  Gavel
} from '@mui/icons-material';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { useSnackbar } from 'notistack';
import { purchaseCarbonCredit } from '../utils/paymentProcessor';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useLocation, useNavigate } from 'react-router-dom';
import { useWebSocket } from '../context/WebSocketProvider';
import RealtimeStatusIndicator, { SyncButton } from '../components/RealtimeStatusIndicator';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  TimeScale,
  Filler
} from 'chart.js';
import { Bar, Pie, Line, Doughnut } from 'react-chartjs-2';
import { format, subDays, startOfMonth, endOfMonth, eachMonthOfInterval, subMonths } from 'date-fns';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  TimeScale,
  Filler
);

const RETIREMENT_API_BASE = process.env.REACT_APP_RETIREMENT_API || 'https://serenity.ing/retirement';
const CERTIFICATE_API_BASE = (
  process.env.REACT_APP_CERTIFICATE_API_BASE ||
  process.env.REACT_APP_API_URL ||
  RETIREMENT_API_BASE
).replace(/\/$/, '');
const EXPLORER_TX_BASE =
  (process.env.REACT_APP_EXPLORER_TX_BASE || 'https://explorer.serenity.energy/tx').replace(/\/$/, '');
const PDF_HEADER_IMAGE = `${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/header.png`;

const PDF_THEME = {
  background: [4, 14, 24],
  backgroundDeep: [3, 9, 16],
  panel: [8, 31, 45],
  panelAlt: [10, 42, 55],
  panelSoft: [13, 55, 63],
  border: [32, 118, 135],
  borderSoft: [18, 76, 92],
  cyan: [30, 205, 235],
  green: [102, 225, 87],
  lime: [160, 238, 83],
  white: [244, 250, 255],
  muted: [157, 184, 196],
  dim: [96, 126, 140],
  ink: [5, 19, 27]
};

const sanitizePdfText = (value) => String(value)
  .replaceAll(String.fromCharCode(0), '')
  .replace(/CO\s*[‚₂]\.?/gi, 'CO2')
  .replace(/COâ‚‚/gi, 'CO2')
  .replace(/\s+/g, ' ')
  .trim();

const toPdfText = (value, fallback = 'N/A') => {
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'object') return sanitizePdfText(JSON.stringify(value));
  return sanitizePdfText(value);
};

const shortenPdfValue = (value, front = 10, back = 8) => {
  const text = toPdfText(value, '');
  if (!text || text.length <= front + back + 3) return text || 'N/A';
  return `${text.slice(0, front)}...${text.slice(-back)}`;
};

const formatPdfDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  return new Date(timestamp * 1000).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  });
};

const formatPdfDateTime = (value) => {
  if (!value) return 'N/A';
  const numericValue = Number(value);
  const date = Number.isFinite(numericValue) && String(value).length <= 10
    ? new Date(numericValue * 1000)
    : new Date(value);

  if (Number.isNaN(date.getTime())) return toPdfText(value);

  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short'
  });
};

const formatPdfMoney = (value, currency = 'AUD') => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 'N/A';
  return `${currency} ${numericValue.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const formatAtomicPdfAmount = (value) => {
  const text = toPdfText(value, '');
  if (!/^\d+$/.test(text) || text.length <= 18) return text || 'N/A';
  const whole = text.slice(0, -18) || '0';
  const fraction = text.slice(-18).replace(/0+$/, '').slice(0, 6);
  return fraction ? `${whole}.${fraction}` : whole;
};

const parseBurnLots = (value) => {
  try {
    const parsed = JSON.parse(toPdfText(value, '[]'));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getPdfPageSize = (doc) => ({
  width: doc.internal.pageSize.getWidth
    ? doc.internal.pageSize.getWidth()
    : doc.internal.pageSize.width,
  height: doc.internal.pageSize.getHeight
    ? doc.internal.pageSize.getHeight()
    : doc.internal.pageSize.height
});

const loadPdfImage = async (src) => {
  try {
    const response = await fetch(src);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('PDF image load failed:', error);
    return null;
  }
};

const setPdfColor = (doc, method, color) => {
  doc[method](color[0], color[1], color[2]);
};

const drawPdfBackground = (doc) => {
  const { width, height } = getPdfPageSize(doc);
  setPdfColor(doc, 'setFillColor', PDF_THEME.background);
  doc.rect(0, 0, width, height, 'F');

  setPdfColor(doc, 'setFillColor', PDF_THEME.backgroundDeep);
  doc.triangle(0, height * 0.58, width, height * 0.25, width, height, 'F');

  doc.setLineWidth(0.15);
  setPdfColor(doc, 'setDrawColor', PDF_THEME.borderSoft);
  for (let x = -28; x < width; x += 26) {
    doc.line(x, height, x + 72, height - 76);
  }

  setPdfColor(doc, 'setDrawColor', PDF_THEME.panelSoft);
  doc.line(0, 46, width, 70);
  doc.line(0, 47.5, width, 71.5);
};

const drawPdfHeroHeader = (doc, headerImageDataUrl, height = 44) => {
  const { width } = getPdfPageSize(doc);
  if (headerImageDataUrl) {
    doc.addImage(headerImageDataUrl, 'PNG', 0, 0, width, height);
  } else {
    setPdfColor(doc, 'setFillColor', PDF_THEME.panel);
    doc.rect(0, 0, width, height, 'F');
    setPdfColor(doc, 'setTextColor', PDF_THEME.white);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('Carbon Certificate Vault', 14, 24);
  }

  setPdfColor(doc, 'setFillColor', PDF_THEME.backgroundDeep);
  doc.rect(0, height - 4, width, 4, 'F');
  doc.setLineWidth(0.7);
  setPdfColor(doc, 'setDrawColor', PDF_THEME.green);
  doc.line(0, height, width, height);
};

const drawPdfMiniHeader = (doc, title, subtitle) => {
  const { width } = getPdfPageSize(doc);
  drawPdfBackground(doc);
  setPdfColor(doc, 'setFillColor', PDF_THEME.panel);
  doc.rect(0, 0, width, 24, 'F');
  doc.setLineWidth(0.45);
  setPdfColor(doc, 'setDrawColor', PDF_THEME.cyan);
  doc.line(14, 24, width - 14, 24);
  setPdfColor(doc, 'setTextColor', PDF_THEME.white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(title, 14, 15);
  setPdfColor(doc, 'setTextColor', PDF_THEME.muted);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(subtitle, width - 14, 15, { align: 'right' });
};

const drawPdfCard = (doc, x, y, width, height, options = {}) => {
  const {
    fill = PDF_THEME.panel,
    stroke = PDF_THEME.borderSoft,
    accent = PDF_THEME.cyan,
    radius = 3
  } = options;

  setPdfColor(doc, 'setFillColor', fill);
  doc.roundedRect(x, y, width, height, radius, radius, 'F');
  doc.setLineWidth(0.22);
  setPdfColor(doc, 'setDrawColor', stroke);
  doc.roundedRect(x, y, width, height, radius, radius, 'S');
  doc.setLineWidth(0.55);
  setPdfColor(doc, 'setDrawColor', accent);
  doc.line(x + 4, y + 1.2, x + width - 4, y + 1.2);
};

const drawPdfPill = (doc, x, y, width, height, text, options = {}) => {
  const {
    fill = PDF_THEME.green,
    stroke = PDF_THEME.lime,
    color = PDF_THEME.ink
  } = options;

  setPdfColor(doc, 'setFillColor', fill);
  doc.roundedRect(x, y, width, height, height / 2, height / 2, 'F');
  doc.setLineWidth(0.2);
  setPdfColor(doc, 'setDrawColor', stroke);
  doc.roundedRect(x, y, width, height, height / 2, height / 2, 'S');
  setPdfColor(doc, 'setTextColor', color);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.text(text, x + width / 2, y + height / 2 + 2.1, { align: 'center' });
};

const fitPdfLines = (doc, text, maxWidth, maxLines) => {
  const lines = doc.splitTextToSize(toPdfText(text), maxWidth);
  if (!maxLines || lines.length <= maxLines) return lines;
  const fitted = lines.slice(0, maxLines);
  const last = fitted[fitted.length - 1];
  fitted[fitted.length - 1] = `${last.slice(0, Math.max(0, last.length - 3))}...`;
  return fitted;
};

const drawPdfInfoCard = (doc, x, y, width, height, label, value, options = {}) => {
  const {
    mono = false,
    maxLines = 2,
    accent = PDF_THEME.cyan,
    fill = PDF_THEME.panel,
    valueSize = mono ? 7 : 8.2
  } = options;

  drawPdfCard(doc, x, y, width, height, { fill, accent });
  setPdfColor(doc, 'setTextColor', accent);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.8);
  doc.text(label.toUpperCase(), x + 4.5, y + 7.2);
  setPdfColor(doc, 'setTextColor', PDF_THEME.white);
  doc.setFont(mono ? 'courier' : 'helvetica', 'normal');
  doc.setFontSize(valueSize);
  const lines = fitPdfLines(doc, value, width - 9, maxLines);
  doc.text(lines, x + 4.5, y + 14.4);
};

const drawPdfFormTextField = (doc, options) => {
  const {
    fieldName,
    x,
    y,
    width,
    height,
    fontName = 'helvetica',
    fontStyle = 'normal',
    fontSize = 9,
    maxLength,
    textAlign = 'left'
  } = options;

  setPdfColor(doc, 'setFillColor', PDF_THEME.backgroundDeep);
  doc.roundedRect(x, y, width, height, 1.8, 1.8, 'F');
  doc.setLineWidth(0.35);
  setPdfColor(doc, 'setDrawColor', PDF_THEME.border);
  doc.roundedRect(x, y, width, height, 1.8, 1.8, 'S');
  doc.setLineWidth(0.45);
  setPdfColor(doc, 'setDrawColor', PDF_THEME.cyan);
  doc.line(x, y + height + 1.7, x + width, y + height + 1.7);

  const textField = new doc.AcroForm.TextField();
  textField.fieldName = fieldName;
  textField.x = x + 1.3;
  textField.y = y + 1.3;
  textField.width = width - 2.6;
  textField.height = height - 2.6;
  textField.value = '';
  textField.defaultValue = '';
  textField.fontName = fontName;
  textField.fontStyle = fontStyle;
  textField.fontSize = fontSize;
  textField.maxFontSize = fontSize;
  textField.color = '#ffffff';
  textField.textAlign = textAlign;
  textField.showWhenPrinted = true;
  textField.doNotSpellCheck = true;
  if (maxLength) textField.maxLength = maxLength;

  const getWhiteDefaultAppearance = (scope, fieldFontSize = fontSize) => {
    const fieldFont = scope?.internal?.getFont(fontName, fontStyle);
    const fieldFontId = fieldFont?.id || 'F1';
    return `/${fieldFontId} ${fieldFontSize} Tf 1 1 1 rg`;
  };

  const originalGetKeyValueListForStream = textField.getKeyValueListForStream.bind(textField);
  textField.getKeyValueListForStream = function getWhiteTextFieldKeyValueList() {
    const keyValueList = originalGetKeyValueListForStream();
    const fieldFontSize = this.fontSize || fontSize;

    return [
      ...keyValueList.filter((entry) => entry.key !== 'DA'),
      { key: 'DA', value: `(${getWhiteDefaultAppearance(this.scope, fieldFontSize)})` }
    ];
  };

  doc.addField(textField);

  const acroFormRoot = doc.internal?.acroformPlugin?.acroFormDictionaryRoot;
  if (acroFormRoot) {
    acroFormRoot.DA = getWhiteDefaultAppearance(doc);
    if (!Object.prototype.hasOwnProperty.call(acroFormRoot, 'NeedAppearances')) {
      Object.defineProperty(acroFormRoot, 'NeedAppearances', {
        value: true,
        enumerable: false,
        configurable: false
      });
    }
  }
};

const drawPdfFooter = (doc, pageNumber, pageCount, label) => {
  const { width, height } = getPdfPageSize(doc);
  doc.setLineWidth(0.18);
  setPdfColor(doc, 'setDrawColor', PDF_THEME.borderSoft);
  doc.line(14, height - 17, width - 14, height - 17);
  setPdfColor(doc, 'setTextColor', PDF_THEME.dim);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.2);
  doc.text(`${label} | Page ${pageNumber} of ${pageCount}`, 14, height - 10);
  doc.text('serenity.ing/retirement', width - 14, height - 10, { align: 'right' });
};

const getPdfFilenameFromDisposition = (disposition, fallback) => {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition || '');
  return match ? decodeURIComponent(match[1]) : fallback;
};

const readPdfExportError = async (error) => {
  const payload = error?.response?.data;
  if (payload instanceof Blob) {
    const text = await payload.text();
    try {
      return JSON.parse(text)?.error || text || error.message;
    } catch {
      return text || error.message;
    }
  }
  return error?.response?.data?.error || error.message || 'Signed PDF export failed';
};

const parseRegistryMetadata = (metadata) => {
  if (!metadata) return {};
  if (typeof metadata === 'object') return metadata;
  try {
    return JSON.parse(metadata);
  } catch {
    return {};
  }
};

const getRegistryMetadataAttribute = (metadata, name) => (
  metadata?.attributes?.find((attr) => attr?.trait_type === name)?.value
);

const formatRegistryCarbonAmount = (amount) => {
  if (amount === null || amount === undefined || amount === '') return '0.00';
  const asNumber = Number(amount);
  if (!Number.isFinite(asNumber)) return String(amount);
  if (asNumber > 1000000000000000) return (asNumber / 1e18).toFixed(2);
  return asNumber.toFixed(2);
};

const normalizeRegistryRetirementRecord = (record, tokenIdFallback) => {
  const metadata = parseRegistryMetadata(record?.metadata);
  const timestamp = Number(
    record?.timestamp ||
    getRegistryMetadataAttribute(metadata, 'Retirement Timestamp') ||
    0
  );
  const amount = record?.amount || getRegistryMetadataAttribute(metadata, 'Carbon Tokens Burned');

  return {
    ...record,
    tokenId: record?.tokenId ?? tokenIdFallback,
    metadata,
    timestamp,
    name: metadata?.name || record?.name || `Carbon Credit RETIRED Certificate #${record?.tokenId ?? tokenIdFallback}`,
    description: metadata?.description || record?.description || '',
    device: record?.device || getRegistryMetadataAttribute(metadata, 'Device ID') || null,
    retiredBy: record?.retiredBy || getRegistryMetadataAttribute(metadata, 'Retired By') || null,
    txHash: record?.txHash || getRegistryMetadataAttribute(metadata, 'Retire Tx Hash') || null,
    amount,
    amountFormatted: record?.amountFormatted || formatRegistryCarbonAmount(amount)
  };
};

const scrollRegistryDeepLinkIntoView = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const headerShell = document.querySelector('.App-header-shell');
  const headerHeight = headerShell?.getBoundingClientRect?.().height || 260;
  window.scrollTo({
    top: Math.max(0, headerHeight - 16),
    behavior: 'auto'
  });
};

function RegistryHome() {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const location = useLocation?.() || { search: '' };
  const navigate = useNavigate?.();
  
  // WebSocket integration with error handling
  const webSocketContext = useWebSocket();
  const { 
    isConnected: wsConnected = false, 
    retirements: wsRetirements = [], 
    stats: wsStats = null, 
    lastRetirement = null, 
    requestSync = () => {}, 
    addRetirement = () => {}, 
    updateStats = () => {},
    isInitialized = false
  } = webSocketContext || {};
  
  // State management
  const [retirements, setRetirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Analytics state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  
  // Real-time state
  const [newRetirementsCount, setNewRetirementsCount] = useState(0);
  const [showNewDataAlert, setShowNewDataAlert] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  // Purchased tokens (local persistence)
  const [purchasedTokens, setPurchasedTokens] = useState(() => {
    try {
      const raw = localStorage.getItem('purchased_tokens');
      const arr = raw ? JSON.parse(raw) : [];
      if (Array.isArray(arr)) return new Set(arr.map(String));
    } catch {}
    return new Set();
  });

  const markPurchased = useCallback((tokenId) => {
    if (tokenId == null) return;
    const idStr = String(tokenId);
    setPurchasedTokens(prev => {
      if (prev.has(idStr)) return prev;
      const next = new Set(prev);
      next.add(idStr);
      try { localStorage.setItem('purchased_tokens', JSON.stringify(Array.from(next))); } catch {}
      return next;
    });
  }, []);

  const isPurchased = useCallback((tokenId) => {
    if (tokenId == null) return false;
    return purchasedTokens.has(String(tokenId));
  }, [purchasedTokens]);

  // Handle payment redirect params (?payment=success&tokenId=18)
  useEffect(() => {
    try {
      const qs = new URLSearchParams(location?.search || window.location.search);
      const status = qs.get('payment');
      const tokenId = qs.get('tokenId');
      const sessionId = qs.get('session_id');
      if (status === 'success' && tokenId) {
        // Attempt server-side confirmation if we have a Stripe session id
        const confirmIfNeeded = async () => {
          try {
            if (sessionId) {
              const confirmedKey = `confirmed_session_${sessionId}`;
              const already = sessionStorage.getItem(confirmedKey);
              if (!already) {
                const base = process.env.REACT_APP_PAYMENT_API || 'https://serenity.ing/payments';
                const url = `${(base || '').replace(/\/$/, '')}/confirm?session_id=${encodeURIComponent(sessionId)}`;
                const res = await fetch(url, { method: 'GET' });
                if (res.ok) sessionStorage.setItem(confirmedKey, '1');
              }
            }
          } catch (e) {
            console.warn('Purchase confirm failed:', e.message);
          }
        };
        confirmIfNeeded().finally(() => {
          // Update local and try fetching purchaser details
          markPurchased(tokenId);
          fetchPurchaserDetails?.(tokenId);
          enqueueSnackbar?.(`Payment successful for Certificate #${tokenId}.`, { variant: 'success' });
          if (navigate) navigate('/registry', { replace: true });
        });
      } else if (status === 'cancel') {
        enqueueSnackbar?.('Payment was cancelled.', { variant: 'info' });
        if (navigate) navigate('/registry', { replace: true });
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.search]);
  
  // Filter and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Enhanced Search State
  const [searchMode, setSearchMode] = useState('simple'); // 'simple', 'advanced', 'query-builder'
  const [globalSearch, setGlobalSearch] = useState('');
  const [searchHighlights, setSearchHighlights] = useState({});
  const [searchHistory, setSearchHistory] = useState([]);
  const [savedSearches, setSavedSearches] = useState([]);
  const [quickFilters, setQuickFilters] = useState([]);
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const [showSavedSearches, setShowSavedSearches] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Advanced Query Builder State
  const [queryBuilder, setQueryBuilder] = useState({
    rules: [{ field: '', operator: '', value: '', logic: 'AND' }],
    logic: 'AND' // Global logic
  });
  
  // Smart Filter State
  const [smartFilters, setSmartFilters] = useState({
    recentRetirements: false,    // Last 30 days
    largeRetirements: false,     // > 1000 tons
    popularDevices: false,       // Top 10 devices
    verifiedOnly: false,         // Verified certificates only
    thisYear: false,             // Current year
    thisMonth: false             // Current month
  });
  const [filters, setFilters] = useState({
    deviceFilter: '',
    dateFrom: '',
    dateTo: '',
    minAmount: '',
    maxAmount: '',
    sortBy: 'timestamp',
    sortOrder: 'desc'
  });
  
  // Dialog state
  const [selectedRetirement, setSelectedRetirement] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [purchaserDetails, setPurchaserDetails] = useState(null);
  const [loadingPurchaseInfo, setLoadingPurchaseInfo] = useState(false);

  // Helper: fetch purchaser details from backend by tokenId
  const fetchPurchaserDetails = useCallback(async (tokenId) => {
    if (!tokenId) return null;
    try {
      setLoadingPurchaseInfo(true);
      const base = process.env.REACT_APP_PAYMENT_API || 'https://serenity.ing/payments';
      const endpoint = `${(base || '').replace(/\/$/, '')}/status?tokenId=${encodeURIComponent(tokenId)}`;
      const res = await fetch(endpoint, { method: 'GET' });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      if (data?.purchased) {
        setPurchaserDetails(data.details || {});
        // Also mark purchased locally to keep UI consistent
        markPurchased(tokenId);
        return data.details || {};
      }
      setPurchaserDetails(null);
      return null;
    } catch (e) {
      console.warn('Failed to load purchaser details:', e.message);
      return null;
    } finally {
      setLoadingPurchaseInfo(false);
    }
  }, [markPurchased]);

  // Fetch analytics data
  const fetchAnalyticsData = async () => {
    setAnalyticsLoading(true);
    try {
      // Fetch detailed analytics data
      const [timelineResponse, devicesResponse, retirementsResponse] = await Promise.all([
        // Get monthly timeline data for the last 12 months
        axios.get(`${RETIREMENT_API_BASE}/api/retirements`, {
          params: {
            page: 1,
            limit: 1000, // Get all for analytics
            sortBy: 'timestamp',
            sortOrder: 'asc'
          }
        }),
        // Get device-specific data
        axios.get(`${RETIREMENT_API_BASE}/api/retirements`, {
          params: {
            page: 1,
            limit: 1000,
            sortBy: 'device',
            sortOrder: 'asc'
          }
        }),
        // Get recent retirements for trends
        axios.get(`${RETIREMENT_API_BASE}/api/retirements`, {
          params: {
            page: 1,
            limit: 100,
            sortBy: 'timestamp',
            sortOrder: 'desc'
          }
        })
      ]);

      if (timelineResponse.data.success && devicesResponse.data.success && retirementsResponse.data.success) {
        const allRetirements = timelineResponse.data.data;
        const recentRetirements = retirementsResponse.data.data;
        
        // Process timeline data (monthly aggregation)
        const monthlyData = processMonthlyData(allRetirements);
        
        // Process device distribution
        const deviceData = processDeviceData(allRetirements);
        
        // Process retirement amounts distribution
        const amountData = processAmountDistribution(allRetirements);
        
        // Process recent trends
        const trendData = processTrendData(recentRetirements);
        
        // Process hourly patterns
        const hourlyData = processHourlyData(allRetirements);
        
        setAnalyticsData({
          monthly: monthlyData,
          devices: deviceData,
          amounts: amountData,
          trends: trendData,
          hourly: hourlyData,
          totalRecords: allRetirements.length
        });
      }
    } catch (error) {
      console.error('Failed to fetch analytics data:', error);
      enqueueSnackbar('Failed to load analytics data', { variant: 'error' });
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Analytics data processing functions
  const processMonthlyData = (retirements) => {
    const monthlyStats = {};
    
    retirements.forEach(retirement => {
      const date = new Date(retirement.timestamp * 1000);
      const monthKey = format(date, 'yyyy-MM');
      
      if (!monthlyStats[monthKey]) {
        monthlyStats[monthKey] = {
          count: 0,
          totalAmount: 0,
          month: format(date, 'MMM yyyy')
        };
      }
      
      monthlyStats[monthKey].count += 1;
      monthlyStats[monthKey].totalAmount += parseFloat(retirement.amountFormatted || 0);
    });
    
    // Sort by month and get last 12 months
    const sortedMonths = Object.entries(monthlyStats)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-12);
    
    return {
      labels: sortedMonths.map(([_, data]) => data.month),
      counts: sortedMonths.map(([_, data]) => data.count),
      amounts: sortedMonths.map(([_, data]) => data.totalAmount)
    };
  };

  const processDeviceData = (retirements) => {
    const deviceStats = {};
    
    retirements.forEach(retirement => {
      const device = retirement.device || 'Unknown Device';
      if (!deviceStats[device]) {
        deviceStats[device] = { count: 0, totalAmount: 0 };
      }
      deviceStats[device].count += 1;
      deviceStats[device].totalAmount += parseFloat(retirement.amountFormatted || 0);
    });
    
    // Sort by count and take top 10
    const sortedDevices = Object.entries(deviceStats)
      .sort(([, a], [, b]) => b.count - a.count)
      .slice(0, 10);
    
    return {
      labels: sortedDevices.map(([device]) => device),
      counts: sortedDevices.map(([, data]) => data.count),
      amounts: sortedDevices.map(([, data]) => data.totalAmount)
    };
  };

  const processAmountDistribution = (retirements) => {
    // Get the last 12 months' retirements
    // Group by quarter (Q3 2024, Q4 2024, Q1 2025, Q2 2025)
    const quarters = {};
    retirements.forEach(r => {
      const date = new Date(r.timestamp * 1000);
      const year = date.getFullYear();
      const month = date.getMonth(); // 0 = Jan
      const q = Math.floor(month / 3) + 1;
      const label = `Q${q} ${year}`;
      if (!quarters[label]) quarters[label] = 0;
      quarters[label]++;
    });

    // Get the last 4 quarters, sorted chronologically
    const sorted = Object.entries(quarters)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-4);

    return {
      labels: sorted.map(([label]) => label),
      data: sorted.map(([, count]) => count)
    };  
  };

  const processTrendData = (recentRetirements) => {
    const last30Days = recentRetirements.filter(retirement => {
      const retirementDate = new Date(retirement.timestamp * 1000);
      const thirtyDaysAgo = subDays(new Date(), 30);
      return retirementDate >= thirtyDaysAgo;
    });
    
    const dailyStats = {};
    for (let i = 29; i >= 0; i--) {
      const date = subDays(new Date(), i);
      const dateKey = format(date, 'yyyy-MM-dd');
      dailyStats[dateKey] = { count: 0, amount: 0, label: format(date, 'MMM dd') };
    }
    
    last30Days.forEach(retirement => {
      const dateKey = format(new Date(retirement.timestamp * 1000), 'yyyy-MM-dd');
      if (dailyStats[dateKey]) {
        dailyStats[dateKey].count += 1;
        dailyStats[dateKey].amount += parseFloat(retirement.amountFormatted || 0);
      }
    });
    
    const sortedDays = Object.entries(dailyStats).sort(([a], [b]) => a.localeCompare(b));
    
    return {
      labels: sortedDays.map(([_, data]) => data.label),
      counts: sortedDays.map(([_, data]) => data.count),
      amounts: sortedDays.map(([_, data]) => data.amount)
    };
  };

  const processHourlyData = (retirements) => {
    const hourlyStats = Array(24).fill(0);
    
    retirements.forEach(retirement => {
      const hour = new Date(retirement.timestamp * 1000).getHours();
      hourlyStats[hour]++;
    });
    
    return {
      labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
      data: hourlyStats
    };
  };

  // Fetch registry statistics
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${RETIREMENT_API_BASE}/api/registry/stats`);
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch registry stats:', error);
    }
  };

  // Fetch retirements with filters
  /*
  const fetchRetirements = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: page + 1, // API expects 1-based pagination
        limit: rowsPerPage,
        search: searchTerm,
        ...filters
      };

      const response = await axios.get(`${RETIREMENT_API_BASE}/api/retirements`, { params });
      
      if (response.data.success) {
        setRetirements(response.data.data);
        setTotalRecords(response.data.pagination.totalRecords);
      } else {
        setError('Failed to fetch retirement data');
      }
    } catch (error) {
      console.error('Error fetching retirements:', error);
      setError('Failed to connect to retirement service');
    } finally {
      setLoading(false);
    }
  };
  */
 
  const fetchRetirements = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: page + 1, // API expects 1-based pagination
        limit: rowsPerPage,
        search: searchTerm,
        ...filters
      };

      const response = await axios.get(`${RETIREMENT_API_BASE}/api/retirements`, { params });
      
      if (response.data.success) {
        setRetirements(response.data.data);
        setTotalRecords(response.data.pagination.totalRecords);
      } else {
        setError('Failed to fetch retirement data');
      }
    } catch (error) {
      console.error('Error fetching retirements:', error);
      setError('Failed to connect to retirement service');
    } finally {
      setLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    fetchStats();
    fetchAnalyticsData();
  }, []);

  useEffect(() => {
    fetchRetirements();
  }, [page, rowsPerPage, searchTerm, filters]);

  // WebSocket integration effects
  useEffect(() => {
    // Handle new WebSocket retirements
    if (isInitialized && wsRetirements.length > 0) {
      const newestRetirement = wsRetirements[0];
      
      // Check if this retirement is new (not in current list)
      const exists = retirements.some(r => r.tokenId === newestRetirement.tokenId);
      if (!exists) {
        setRetirements(prev => [newestRetirement, ...prev]);
        setNewRetirementsCount(prev => prev + 1);
        setShowNewDataAlert(true);
        setLastUpdateTime(new Date());
        
        // Auto-refresh analytics when new data arrives
        fetchAnalyticsData();
      }
    }
  }, [wsRetirements, retirements, isInitialized]);

  useEffect(() => {
    // Handle WebSocket stats updates
    if (isInitialized && wsStats) {
      setStats(wsStats);
      setLastUpdateTime(new Date());
    }
  }, [wsStats, isInitialized]);

  useEffect(() => {
    // Handle last retirement updates for notifications
    if (isInitialized && lastRetirement) {
      console.log('[Registry] New retirement notification:', lastRetirement);
    }
  }, [lastRetirement, isInitialized]);

  // Handlers
  const handleSearch = () => {
    setPage(0); // Reset to first page when searching
    fetchRetirements();
  };

  // Enhanced Search Functions
  const handleGlobalSearch = useCallback((term) => {
    setGlobalSearch(term);
    setSearchTerm(term); // Sync with existing search
    
    // Add to search history
    if (term.trim() && !searchHistory.includes(term.trim())) {
      const newHistory = [term.trim(), ...searchHistory.slice(0, 9)]; // Keep last 10
      setSearchHistory(newHistory);
      localStorage.setItem('carbonRegistry_searchHistory', JSON.stringify(newHistory));
    }
    
    // Trigger search
    setPage(0);
    fetchRetirements();
  }, [searchHistory]);

  // Search Highlighting Component
  const HighlightText = ({ text, highlight }) => {
    if (!highlight || !text) return text;
    
    // Handle multiple search terms separated by spaces
    const searchTerms = highlight.toString().split(' ').filter(term => term.trim().length > 0);
    if (searchTerms.length === 0) return text;
    
    let highlightedText = text.toString();
    
    // Apply highlighting for each search term
    searchTerms.forEach(term => {
      if (term.length > 1) { // Only highlight terms with more than 1 character
        const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        highlightedText = highlightedText.replace(regex, (match) => 
          `<mark style="background-color: #fff59d; color: #f57f17; font-weight: bold; padding: 0 2px; border-radius: 2px;">${match}</mark>`
        );
      }
    });
    
    // Only return JSX if highlighting was applied
    if (highlightedText !== text.toString()) {
      return <span dangerouslySetInnerHTML={{ __html: highlightedText }} />;
    }
    
    return text;
  };

  // Count search matches in certificate data
  const countSearchMatches = (retirement, searchTerm) => {
    if (!searchTerm || !retirement) return 0;
    
    const searchTerms = searchTerm.split(' ').filter(term => term.trim().length > 1);
    let totalMatches = 0;
    
    const fieldsToSearch = [
      retirement.tokenId?.toString(),
      retirement.name,
      retirement.description,
      retirement.device,
      retirement.retiredBy,
      retirement.txHash,
      ...(retirement.metadata?.attributes?.map(attr => `${attr.trait_type} ${attr.value}`) || [])
    ];
    
    searchTerms.forEach(term => {
      fieldsToSearch.forEach(field => {
        if (field) {
          const matches = field.toLowerCase().match(new RegExp(term.toLowerCase(), 'g'));
          if (matches) totalMatches += matches.length;
        }
      });
    });
    
    return totalMatches;
  };

  const handleSmartFilter = (filterKey) => {
    setSmartFilters(prev => ({
      ...prev,
      [filterKey]: !prev[filterKey]
    }));
    
    // Apply smart filter logic
    const now = new Date();
    const newFilters = { ...filters };
    
    switch (filterKey) {
      case 'recentRetirements':
        if (!smartFilters.recentRetirements) {
          const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          newFilters.dateFrom = thirtyDaysAgo.toISOString().split('T')[0];
        } else {
          newFilters.dateFrom = '';
        }
        break;
      case 'largeRetirements':
        if (!smartFilters.largeRetirements) {
          newFilters.minAmount = '1000';
        } else {
          newFilters.minAmount = '';
        }
        break;
      case 'thisYear':
        if (!smartFilters.thisYear) {
          newFilters.dateFrom = `${now.getFullYear()}-01-01`;
        } else {
          newFilters.dateFrom = '';
        }
        break;
      case 'thisMonth':
        if (!smartFilters.thisMonth) {
          const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          newFilters.dateFrom = firstOfMonth.toISOString().split('T')[0];
        } else {
          newFilters.dateFrom = '';
        }
        break;
    }
    
    setFilters(newFilters);
  };

  const handleSaveSearch = () => {
    const searchConfig = {
      id: Date.now(),
      name: `Search ${savedSearches.length + 1}`,
      term: globalSearch,
      filters: { ...filters },
      smartFilters: { ...smartFilters },
      timestamp: new Date().toISOString()
    };
    
    const newSavedSearches = [...savedSearches, searchConfig];
    setSavedSearches(newSavedSearches);
    localStorage.setItem('carbonRegistry_savedSearches', JSON.stringify(newSavedSearches));
  };

  const handleLoadSavedSearch = (searchConfig) => {
    setGlobalSearch(searchConfig.term);
    setSearchTerm(searchConfig.term);
    setFilters(searchConfig.filters);
    setSmartFilters(searchConfig.smartFilters);
    setPage(0);
    fetchRetirements();
  };

  const handleDeleteSavedSearch = (searchId) => {
    const newSavedSearches = savedSearches.filter(s => s.id !== searchId);
    setSavedSearches(newSavedSearches);
    localStorage.setItem('carbonRegistry_savedSearches', JSON.stringify(newSavedSearches));
  };

  // Query Builder Functions
  const addQueryRule = () => {
    setQueryBuilder(prev => ({
      ...prev,
      rules: [...prev.rules, { field: '', operator: '', value: '', logic: 'AND' }]
    }));
  };

  const removeQueryRule = (index) => {
    setQueryBuilder(prev => ({
      ...prev,
      rules: prev.rules.filter((_, i) => i !== index)
    }));
  };

  const updateQueryRule = (index, field, value) => {
    setQueryBuilder(prev => ({
      ...prev,
      rules: prev.rules.map((rule, i) => 
        i === index ? { ...rule, [field]: value } : rule
      )
    }));
  };

  const executeQueryBuilder = () => {
    // Convert query builder rules to API filters
    const apiFilters = { ...filters };
    let searchTerms = [];
    
    queryBuilder.rules.forEach(rule => {
      if (rule.field && rule.operator && rule.value) {
        switch (rule.field) {
          case 'device':
            apiFilters.deviceFilter = rule.value;
            break;
          case 'amount':
            if (rule.operator === 'gte') apiFilters.minAmount = rule.value;
            if (rule.operator === 'lte') apiFilters.maxAmount = rule.value;
            break;
          case 'date':
            if (rule.operator === 'gte') apiFilters.dateFrom = rule.value;
            if (rule.operator === 'lte') apiFilters.dateTo = rule.value;
            break;
          case 'description':
          case 'metadata':
            searchTerms.push(rule.value);
            break;
        }
      }
    });
    
    if (searchTerms.length > 0) {
      setGlobalSearch(searchTerms.join(' '));
      setSearchTerm(searchTerms.join(' '));
    }
    
    setFilters(apiFilters);
    setPage(0);
    fetchRetirements();
  };

  const clearAllSearch = () => {
    setGlobalSearch('');
    setSearchTerm('');
    setFilters({
      deviceFilter: '',
      dateFrom: '',
      dateTo: '',
      minAmount: '',
      maxAmount: '',
      sortBy: 'timestamp',
      sortOrder: 'desc'
    });
    setSmartFilters({
      recentRetirements: false,
      largeRetirements: false,
      popularDevices: false,
      verifiedOnly: false,
      thisYear: false,
      thisMonth: false
    });
    setQueryBuilder({
      rules: [{ field: '', operator: '', value: '', logic: 'AND' }],
      logic: 'AND'
    });
    setPage(0);
    fetchRetirements();
  };

  // Generate search suggestions
  const generateSearchSuggestions = useCallback((term) => {
    if (!term || term.length < 2) {
      setSearchSuggestions([]);
      return;
    }
    
    // Create suggestions based on available data
    const suggestions = [];
    
    // Device suggestions
    if (retirements.length > 0) {
      const devices = [...new Set(retirements.map(r => r.device).filter(Boolean))];
      const deviceMatches = devices.filter(d => 
        d.toLowerCase().includes(term.toLowerCase())
      ).slice(0, 3);
      deviceMatches.forEach(device => {
        suggestions.push({
          type: 'device',
          text: device,
          label: `Device: ${device}`,
          action: () => handleGlobalSearch(`device:${device}`)
        });
      });
    }
    
    // Common search terms
    const commonTerms = [
      'solar', 'wind', 'hydro', 'biomass', 'geothermal',
      'verified', 'retirement', 'carbon', 'credit', 'offset'
    ];
    
    const termMatches = commonTerms.filter(t => 
      t.toLowerCase().includes(term.toLowerCase())
    ).slice(0, 2);
    
    termMatches.forEach(termMatch => {
      suggestions.push({
        type: 'term',
        text: termMatch,
        label: `Search: ${termMatch}`,
        action: () => handleGlobalSearch(termMatch)
      });
    });
    
    setSearchSuggestions(suggestions.slice(0, 5));
  }, [retirements]);

  // Load saved data on component mount
  useEffect(() => {
    const savedHistory = localStorage.getItem('carbonRegistry_searchHistory');
    if (savedHistory) {
      setSearchHistory(JSON.parse(savedHistory));
    }
    
    const savedSearches = localStorage.getItem('carbonRegistry_savedSearches');
    if (savedSearches) {
      setSavedSearches(JSON.parse(savedSearches));
    }
  }, []);

  // Generate suggestions when global search changes
  useEffect(() => {
    generateSearchSuggestions(globalSearch);
  }, [globalSearch, generateSearchSuggestions]);

  // Keyboard shortcuts for search
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Ctrl/Cmd + K to focus search
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        const searchInput = document.querySelector('input[placeholder*="Global Search"]');
        if (searchInput) {
          searchInput.focus();
        }
      }
      
      // Escape to clear search highlights
      if (event.key === 'Escape' && globalSearch) {
        setGlobalSearch('');
        setSearchTerm('');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [globalSearch]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page when filtering
  };

  const handleSort = (field) => {
    const isAsc = filters.sortBy === field && filters.sortOrder === 'asc';
    handleFilterChange('sortBy', field);
    handleFilterChange('sortOrder', isAsc ? 'desc' : 'asc');
  };

  const handleViewDetails = useCallback((retirement) => {
    setSelectedRetirement(retirement);
    setDialogOpen(true);
    const tid = retirement?.tokenId;
    // Always query backend for authoritative status/details
    fetchPurchaserDetails(tid);
  }, [fetchPurchaserDetails]);

  const registryDeepLinkRef = useRef('');

  useEffect(() => {
    const qs = new URLSearchParams(location?.search || window.location.search);
    if (qs.get('payment')) return;

    const tokenId = qs.get('tokenId');
    if (!tokenId || !/^\d+$/.test(tokenId)) return;

    const deepLinkKey = `tokenId:${tokenId}`;
    if (registryDeepLinkRef.current === deepLinkKey) return;

    const openDeepLinkedCertificate = (record) => {
      registryDeepLinkRef.current = deepLinkKey;
      setTabValue(0);
      const normalizedRecord = normalizeRegistryRetirementRecord(record, tokenId);
      scrollRegistryDeepLinkIntoView();

      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => handleViewDetails(normalizedRecord));
      } else {
        handleViewDetails(normalizedRecord);
      }
    };

    const existingRecord = retirements.find((retirement) => String(retirement?.tokenId) === tokenId);
    if (existingRecord) {
      openDeepLinkedCertificate(existingRecord);
      return;
    }

    registryDeepLinkRef.current = deepLinkKey;
    let cancelled = false;

    const fetchDeepLinkedCertificate = async () => {
      try {
        const response = await axios.get(
          `${RETIREMENT_API_BASE}/api/retirement/${encodeURIComponent(tokenId)}`
        );
        const record = response.data?.data || response.data?.retirement || response.data;
        if (!cancelled && record) {
          setTabValue(0);
          handleViewDetails(normalizeRegistryRetirementRecord(record, tokenId));
        }
      } catch (error) {
        if (!cancelled) {
          enqueueSnackbar?.(`Certificate #${tokenId} could not be loaded.`, { variant: 'warning' });
        }
      }
    };

    fetchDeepLinkedCertificate();

    return () => {
      cancelled = true;
    };
  }, [location?.search, retirements, handleViewDetails, enqueueSnackbar]);

  const handleOpenNftAudit = useCallback((retirement) => {
    if (!navigate) return;

    const params = new URLSearchParams();
    params.set('tab', 'nft-audit');

    const txHash = String(retirement?.txHash || '').trim();
    const tokenId = retirement?.tokenId;

    if (txHash) params.set('txHash', txHash);
    if (tokenId !== undefined && tokenId !== null && String(tokenId).trim() !== '') {
      params.set('tokenId', String(tokenId));
    }

    navigate(`/provenance?${params.toString()}`);
  }, [navigate]);

  const formatAmount = (amount) => {
    if (!amount) return '0';
    const value = parseFloat(amount);
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const formatAddress = (address) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const resolveConnectedWalletAddress = useCallback(async () => {
    if (!window?.ethereum?.request) {
      throw new Error('MetaMask is not available');
    }

    const isEthAddress = (value) => /^0x[a-fA-F0-9]{40}$/.test(String(value || ''));

    let accounts = [];
    try {
      accounts = await window.ethereum.request({ method: 'eth_accounts' });
    } catch (_) {
      accounts = [];
    }

    if (!Array.isArray(accounts) || accounts.length === 0) {
      accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
    }

    const address = Array.isArray(accounts) ? accounts[0] : null;
    if (!isEthAddress(address)) {
      throw new Error('Please connect a valid MetaMask wallet before purchase');
    }

    return address;
  }, []);

  // Statistics Cards Component
  const StatsCards = () => (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <Verified color="primary" sx={{ mr: 1 }} />
              <Box>
                <Typography variant="h6">{stats?.totalRetirements || 0}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Retirements
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <Timeline color="error" sx={{ mr: 1 }} />
              <Box>
                <Typography variant="h6">{Number(stats?.totalCarbonRetired || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Carbon (burned)
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <Timeline color="success" sx={{ mr: 1 }} />
              <Box>
                <Typography variant="h6">{stats?.totalRetirements || 0}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Total CO₂ Retired (tons)
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <Public color="info" sx={{ mr: 1 }} />
              <Box>
                <Typography variant="h6">{stats?.uniqueDevices || 0}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Unique Devices
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <AccountBalance color="warning" sx={{ mr: 1 }} />
              <Box>
                <Typography variant="h6">{stats?.uniqueRetirers || 0}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Unique Entities
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  // Analytics Components
  const AnalyticsCharts = () => {
    if (analyticsLoading) {
      return (
        <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Loading analytics data...
          </Typography>
        </Paper>
      );
    }

    if (!analyticsData) {
      return (
        <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
          <Analytics sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No analytics data available
          </Typography>
        </Paper>
      );
    }

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            color: theme.palette.text.primary,
            usePointStyle: true,
            padding: 20
          }
        },
        tooltip: {
          backgroundColor: theme.palette.background.paper,
          titleColor: theme.palette.text.primary,
          bodyColor: theme.palette.text.primary,
          borderColor: theme.palette.divider,
          borderWidth: 1
        }
      },
      scales: {
        x: {
          ticks: { color: theme.palette.text.secondary },
          grid: { color: theme.palette.divider, borderColor: theme.palette.divider }
        },
        y: {
          ticks: { color: theme.palette.text.secondary },
          grid: { color: theme.palette.divider, borderColor: theme.palette.divider }
        }
      }
    };

    const pieOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
          labels: {
            color: theme.palette.text.primary,
            usePointStyle: true,
            padding: 15
          }
        },
        tooltip: {
          backgroundColor: theme.palette.background.paper,
          titleColor: theme.palette.text.primary,
          bodyColor: theme.palette.text.primary,
          borderColor: theme.palette.divider,
          borderWidth: 1
        }
      }
    };

    // Monthly Retirement Trends Chart
    const monthlyChart = {
      labels: analyticsData.monthly.labels,
      datasets: [
        {
          label: 'Number of Retirements',
          data: analyticsData.monthly.counts,
          backgroundColor: 'rgba(76, 175, 80, 0.6)',
          borderColor: 'rgba(76, 175, 80, 1)',
          borderWidth: 2,
          yAxisID: 'y'
        },
        {
          label: 'Carbon Amount (tokens)',
          data: analyticsData.monthly.amounts,
          backgroundColor: 'rgba(33, 150, 243, 0.6)',
          borderColor: 'rgba(33, 150, 243, 1)',
          borderWidth: 2,
          yAxisID: 'y1'
        }
      ]
    };

    const monthlyOptions = {
      ...chartOptions,
      scales: {
        ...chartOptions.scales,
        y1: {
          type: 'linear',
          display: true,
          position: 'right',
          ticks: { color: theme.palette.text.secondary },
          grid: { drawOnChartArea: false, color: theme.palette.divider }
        }
      }
    };

    // Device Distribution Chart
    const deviceChart = {
      labels: analyticsData.devices.labels,
      datasets: [{
        label: 'Retirements per Device',
        data: analyticsData.devices.counts,
        backgroundColor: [
          'rgba(255, 99, 132, 0.8)',
          'rgba(54, 162, 235, 0.8)',
          'rgba(255, 205, 86, 0.8)',
          'rgba(75, 192, 192, 0.8)',
          'rgba(153, 102, 255, 0.8)',
          'rgba(255, 159, 64, 0.8)',
          'rgba(199, 199, 199, 0.8)',
          'rgba(83, 102, 255, 0.8)',
          'rgba(255, 99, 255, 0.8)',
          'rgba(99, 255, 132, 0.8)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 205, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(199, 199, 199, 1)',
          'rgba(83, 102, 255, 1)',
          'rgba(255, 99, 255, 1)',
          'rgba(99, 255, 132, 1)'
        ],
        borderWidth: 2
      }]
    };

    // Amount Distribution Chart
    const amountChart = {
      labels: analyticsData.amounts.labels,
      datasets: [{
        label: 'Number of Certificates',
        data: analyticsData.amounts.data,
        backgroundColor: [
          'rgba(76, 175, 80, 0.8)',
          'rgba(33, 150, 243, 0.8)',
          'rgba(255, 152, 0, 0.8)',
          'rgba(156, 39, 176, 0.8)',
          'rgba(244, 67, 54, 0.8)'
        ],
        borderColor: [
          'rgba(76, 175, 80, 1)',
          'rgba(33, 150, 243, 1)',
          'rgba(255, 152, 0, 1)',
          'rgba(156, 39, 176, 1)',
          'rgba(244, 67, 54, 1)'
        ],
        borderWidth: 2
      }]
    };

    // 30-Day Trend Chart
    const trendChart = {
      labels: analyticsData.trends.labels,
      datasets: [
        {
          label: 'Daily Retirements',
          data: analyticsData.trends.counts,
          fill: true,
          backgroundColor: 'rgba(76, 175, 80, 0.2)',
          borderColor: 'rgba(76, 175, 80, 1)',
          borderWidth: 2,
          tension: 0.4
        }
      ]
    };

    // Hourly Activity Chart
    const hourlyChart = {
      labels: analyticsData.hourly.labels,
      datasets: [{
        label: 'Retirements by Hour',
        data: analyticsData.hourly.data,
        backgroundColor: 'rgba(33, 150, 243, 0.6)',
        borderColor: 'rgba(33, 150, 243, 1)',
        borderWidth: 2
      }]
    };

    return (
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Insights color="primary" />
            Advanced Analytics & Insights
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Interactive visualizations and trends based on {analyticsData.totalRecords} retirement records
          </Typography>
        </Box>

        <Grid container spacing={3} sx={{ p: 3 }}>
          {/* Monthly Trends */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Timeline color="primary" />
                  Monthly Retirement Trends (Last 12 Months)
                </Typography>
                <Box sx={{ height: 400, mt: 2 }}>
                  <Bar data={monthlyChart} options={monthlyOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Device Distribution and Amount Distribution */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DeviceHub color="primary" />
                  Top Devices by Retirements
                </Typography>
                <Box sx={{ height: 350, mt: 2 }}>
                  <Doughnut data={deviceChart} options={pieOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Park color="primary" />
                  Retirement Amount Distribution
                </Typography>
                <Box sx={{ height: 350, mt: 2 }}>
                  <Pie data={amountChart} options={pieOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* 30-Day Activity Trend */}
          <Grid item xs={12} lg={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShowChart color="primary" />
                  30-Day Activity Trend
                </Typography>
                <Box sx={{ height: 300, mt: 2 }}>
                  <Line data={trendChart} options={chartOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Hourly Activity Pattern */}
          <Grid item xs={12} lg={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarToday color="primary" />
                  Activity by Hour of Day
                </Typography>
                <Box sx={{ height: 300, mt: 2 }}>
                  <Bar data={hourlyChart} options={chartOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Key Insights Summary */}
          <Grid item xs={12}>
            <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUp />
                  Key Insights & Trends
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Most Active Device
                      </Typography>
                      <Typography variant="h6">
                        {analyticsData.devices.labels[0] || 'N/A'}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {analyticsData.devices.counts[0] || 0} retirements
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Peak Month
                      </Typography>
                      <Typography variant="h6">
                        {analyticsData.monthly.labels[analyticsData.monthly.counts.indexOf(Math.max(...analyticsData.monthly.counts))] || 'N/A'}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {Math.max(...analyticsData.monthly.counts) || 0} retirements
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Avg. Daily Activity (30d)
                      </Typography>
                      <Typography variant="h6">
                        {(analyticsData.trends.counts.reduce((a, b) => a + b, 0) / 30).toFixed(1)}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        retirements per day
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Most Common Range
                      </Typography>
                      <Typography variant="h6">
                        {analyticsData.amounts.labels[analyticsData.amounts.data.indexOf(Math.max(...analyticsData.amounts.data))] || 'N/A'}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {Math.max(...analyticsData.amounts.data) || 0} certificates
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  // Export Functions
  const exportToCSV = async () => {
    try {
      setLoading(true);
      
      // Fetch all retirement data (not just current page)
      const response = await axios.get(`${RETIREMENT_API_BASE}/api/retirements`, {
        params: {
          page: 1,
          limit: 1000, // Get a large number to export all
          search: searchTerm,
          ...filters
        }
      });

      if (response.data.success) {
        const data = response.data.data;
        
        // Prepare CSV content
        const csvContent = [
          // CSV Header
          ['Certificate ID', 'Retirement Date', 'Amount (tokens)', 'Device ID', 'Retired By', 'Transaction Hash', 'Issuer', 'Status'].join(','),
          
          // CSV Data rows
          ...data.map(retirement => [
            `#${retirement.tokenId}`,
            new Date(retirement.timestamp * 1000).toLocaleDateString(),
            retirement.amountFormatted,
            retirement.device || 'N/A',
            retirement.retiredBy,
            retirement.txHash || 'N/A',
            retirement.metadata?.attributes?.find(a => a.trait_type === "Issuer")?.value || 'N/A',
            'RETIRED'
          ].map(field => `"${field}"`).join(','))
        ].join('\n');

        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const fileName = `carbon-registry-export-${new Date().toISOString().split('T')[0]}.csv`;
        saveAs(blob, fileName);
        
        enqueueSnackbar(`Successfully exported ${data.length} records to CSV`, { variant: 'success' });
        console.log(`Exported ${data.length} records to CSV`);
      }
    } catch (error) {
      console.error('Export to CSV failed:', error);
      enqueueSnackbar(`Failed to export CSV: ${error.message}`, { variant: 'error' });
      setError('Failed to export data to CSV');
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = async () => {
    try {
      setLoading(true);
      
      const jsPDF = (await import('jspdf')).default;
      const headerImageDataUrl = await loadPdfImage(PDF_HEADER_IMAGE);
      
      // Fetch all retirement data
      const response = await axios.get(`${RETIREMENT_API_BASE}/api/retirements`, {
        params: {
          page: 1,
          limit: 1000,
          search: searchTerm,
          ...filters
        }
      });

      if (response.data.success) {
        const data = response.data.data;
        
        const doc = new jsPDF('p', 'mm', 'a4');
        const { width: pageWidth, height: pageHeight } = getPdfPageSize(doc);
        const margin = 14;
        const usableWidth = pageWidth - margin * 2;
        const generatedAt = new Date();
        const filterSummary = [];

        if (searchTerm) filterSummary.push(`Search: "${searchTerm}"`);
        if (filters.deviceFilter) filterSummary.push(`Device: ${filters.deviceFilter}`);
        if (filters.dateFrom || filters.dateTo) {
          filterSummary.push(`Date: ${filters.dateFrom || 'Any'} to ${filters.dateTo || 'Any'}`);
        }
        if (filters.minAmount || filters.maxAmount) {
          filterSummary.push(`Amount: ${filters.minAmount || '0'} to ${filters.maxAmount || 'Any'} tons`);
        }

        const drawTableHeader = (y) => {
          setPdfColor(doc, 'setFillColor', PDF_THEME.green);
          doc.roundedRect(margin, y, usableWidth, 8.5, 2.5, 2.5, 'F');
          setPdfColor(doc, 'setTextColor', PDF_THEME.ink);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(7.2);
          doc.text('ID', margin + 3, y + 5.6);
          doc.text('Date', margin + 21, y + 5.6);
          doc.text('Tonnes', margin + 48, y + 5.6);
          doc.text('Device', margin + 78, y + 5.6);
          doc.text('Retired By', margin + 116, y + 5.6);
          doc.text('Status', margin + 161, y + 5.6);
          return y + 12;
        };

        const drawReportPage = (title = 'Registry Records') => {
          drawPdfMiniHeader(doc, title, 'Blockchain verified retirement records');
          return drawTableHeader(34);
        };

        drawPdfBackground(doc);
        drawPdfHeroHeader(doc, headerImageDataUrl, 44);

        let yPosition = 57;
        setPdfColor(doc, 'setTextColor', PDF_THEME.white);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(23);
        doc.text('Carbon Registry Report', margin, yPosition);
        drawPdfPill(doc, pageWidth - margin - 43, yPosition - 8, 43, 8.5, 'VERIFIED');

        yPosition += 8;
        setPdfColor(doc, 'setTextColor', PDF_THEME.muted);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9.5);
        doc.text('Retired carbon credit records with public audit provenance.', margin, yPosition);
        doc.text(
          `Generated ${generatedAt.toLocaleDateString()} ${generatedAt.toLocaleTimeString()}`,
          pageWidth - margin,
          yPosition,
          { align: 'right' }
        );

        yPosition += 15;
        const statCardWidth = (usableWidth - 9) / 4;
        drawPdfInfoCard(doc, margin, yPosition, statCardWidth, 23, 'Records', data.length, { accent: PDF_THEME.green, valueSize: 12 });
        drawPdfInfoCard(
          doc,
          margin + statCardWidth + 3,
          yPosition,
          statCardWidth,
          23,
          'CO2 Retired',
          `${Number(stats?.totalCarbonRetired || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} t`,
          { accent: PDF_THEME.lime, valueSize: 10 }
        );
        drawPdfInfoCard(
          doc,
          margin + (statCardWidth + 3) * 2,
          yPosition,
          statCardWidth,
          23,
          'Devices',
          stats?.uniqueDevices || 0,
          { accent: PDF_THEME.cyan, valueSize: 12 }
        );
        drawPdfInfoCard(
          doc,
          margin + (statCardWidth + 3) * 3,
          yPosition,
          statCardWidth,
          23,
          'Entities',
          stats?.uniqueRetirers || 0,
          { accent: PDF_THEME.green, valueSize: 12 }
        );

        yPosition += 31;
        drawPdfCard(doc, margin, yPosition, usableWidth, filterSummary.length ? 22 : 15, {
          fill: PDF_THEME.panelAlt,
          accent: PDF_THEME.lime
        });
        setPdfColor(doc, 'setTextColor', PDF_THEME.lime);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.5);
        doc.text('REPORT SCOPE', margin + 5, yPosition + 7);
        setPdfColor(doc, 'setTextColor', PDF_THEME.white);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.2);
        const scopeText = filterSummary.length
          ? filterSummary.join('   |   ')
          : `All records sorted by ${filters.sortBy} (${filters.sortOrder})`;
        doc.text(doc.splitTextToSize(scopeText, usableWidth - 10), margin + 5, yPosition + 13.5);

        yPosition += filterSummary.length ? 31 : 24;
        yPosition = drawTableHeader(yPosition);

        data.forEach((retirement, index) => {
          if (yPosition > pageHeight - 26) {
            doc.addPage();
            yPosition = drawReportPage('Registry Records Continued');
          }

          setPdfColor(doc, 'setFillColor', index % 2 === 0 ? PDF_THEME.panel : PDF_THEME.panelAlt);
          doc.roundedRect(margin, yPosition - 2, usableWidth, 8.5, 1.6, 1.6, 'F');
          setPdfColor(doc, 'setTextColor', PDF_THEME.white);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7.2);
          doc.text(`#${retirement.tokenId}`, margin + 3, yPosition + 3.6);
          doc.text(formatPdfDate(retirement.timestamp), margin + 21, yPosition + 3.6);
          doc.text(toPdfText(retirement.amountFormatted), margin + 48, yPosition + 3.6);
          doc.setFont('courier', 'normal');
          doc.setFontSize(6.6);
          doc.text(shortenPdfValue(retirement.device, 8, 6), margin + 78, yPosition + 3.6);
          doc.text(shortenPdfValue(retirement.retiredBy, 8, 6), margin + 116, yPosition + 3.6);
          doc.setFont('helvetica', 'bold');
          setPdfColor(doc, 'setTextColor', PDF_THEME.green);
          doc.text('RETIRED', margin + 161, yPosition + 3.6);
          yPosition += 9.6;
        });

        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          drawPdfFooter(doc, i, pageCount, 'All records are blockchain verified and immutable');
        }

        // Save PDF
        const fileName = `carbon-registry-report-${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
        
        enqueueSnackbar(`Successfully exported ${data.length} records to PDF`, { variant: 'success' });
        console.log(`Successfully exported ${data.length} records to PDF`);
      }
    } catch (error) {
      console.error('Export to PDF failed:', error);
      enqueueSnackbar(`Failed to export PDF: ${error.message}`, { variant: 'error' });
      setError(`Failed to export data to PDF: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Export single certificate functions
  const exportCertificateToCSV = (retirement) => {
    try {
      const csvContent = [
        // CSV Header
        ['Field', 'Value'].join(','),
        
        // Certificate data
        ['Certificate ID', `#${retirement.tokenId}`],
        ['Certificate Name', retirement.name],
        ['Description', retirement.description],
        ['Carbon Amount Retired (tons CO₂)', retirement.amountFormatted],
        ['Retirement Date', new Date(retirement.timestamp * 1000).toLocaleDateString()],
        ['Device ID', retirement.device || 'N/A'],
        ['Retired By', retirement.retiredBy],
        ['Transaction Hash', retirement.txHash || 'N/A'],
        ['Issuer', retirement.metadata?.attributes?.find(a => a.trait_type === "Issuer")?.value || 'N/A'],
        ['Status', 'PERMANENTLY RETIRED'],
        
        // Add all metadata attributes
        ...(retirement.metadata?.attributes || []).map(attr => [
          attr.trait_type,
          attr.value
        ])
      ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const fileName = `certificate-${retirement.tokenId}-details.csv`;
      saveAs(blob, fileName);
      
      enqueueSnackbar(`Certificate #${retirement.tokenId} exported to CSV`, { variant: 'success' });
    } catch (error) {
      console.error('Export certificate to CSV failed:', error);
      enqueueSnackbar(`Failed to export certificate: ${error.message}`, { variant: 'error' });
    }
  };

  const exportSignedCertificateToPDF = async (retirement) => {
    const tokenId = retirement?.tokenId;
    if (tokenId === undefined || tokenId === null) {
      throw new Error('Certificate tokenId is missing');
    }

    const response = await axios.get(
      `${CERTIFICATE_API_BASE}/api/certificates/${encodeURIComponent(tokenId)}/signed-pdf`,
      {
        responseType: 'blob'
      }
    );

    const signedHeader = response.headers?.['x-certificate-pdf-signed'];
    const fallbackName = `certificate-${tokenId}-details-signed.pdf`;
    const fileName = getPdfFilenameFromDisposition(response.headers?.['content-disposition'], fallbackName);
    saveAs(response.data, fileName);

    if (signedHeader === 'false') {
      enqueueSnackbar(`Certificate #${tokenId} exported without digital signature`, { variant: 'warning' });
    } else {
      enqueueSnackbar(`Certificate #${tokenId} exported as digitally signed PDF`, { variant: 'success' });
    }
  };

  const exportCertificateToPDF = async (retirement) => {
    try {
      setLoading(true);
      await exportSignedCertificateToPDF(retirement);
    } catch (error) {
      const message = await readPdfExportError(error);
      console.error('Signed certificate PDF export failed:', error);

      if (process.env.REACT_APP_ENABLE_CLIENT_PDF_FALLBACK === 'true') {
        enqueueSnackbar(`Signed PDF unavailable, using browser PDF fallback: ${message}`, { variant: 'warning' });
        await exportClientCertificateToPDF(retirement);
      } else {
        enqueueSnackbar(`Failed to export signed certificate: ${message}`, { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const exportClientCertificateToPDF = async (retirement) => {
    try {
      const jsPDF = (await import('jspdf')).default;
      const headerImageDataUrl = await loadPdfImage(PDF_HEADER_IMAGE);
      const attributes = retirement.metadata?.attributes || [];
      const findAttribute = (name) => attributes.find(attr => attr.trait_type === name)?.value;
      const issuer = findAttribute('Issuer') || 'Serenity Registry';
      const mintType = findAttribute('Mint Type') || 'N/A';
      const blockNumber = findAttribute('Mint Block Number') || findAttribute('Retire Block Number') || 'N/A';
      const mintMessageId = findAttribute('Mint MessageId');
      const provenanceRoot = findAttribute('Mint ProvenanceRoot');
      const mintTxHash = findAttribute('Mint Tx Hash');
      const mintTimestamp = findAttribute('Mint Timestamp');
      const burnLots = parseBurnLots(findAttribute('Burn Plan Lots'));
      const priceSource = findAttribute('Source');
      const retirementIsoDate = findAttribute('Retirement Date');
      const atomicCarbonBurned = findAttribute('Carbon Tokens Burned');
      const amount = toPdfText(retirement.amountFormatted);
      const retirementDate = formatPdfDate(retirement.timestamp);
      const generatedAt = new Date();
      const qrTarget = (() => {
        if (purchaserDetails?.metadataIpfs) {
          const gw = (process.env.REACT_APP_IPFS_GATEWAY || 'https://serenity.ing/ipfs').replace(/\/$/, '');
          return `${gw}/${purchaserDetails.metadataIpfs}`;
        }
        const origin = typeof window !== 'undefined' ? window.location.origin : 'https://serenity.ing';
        return `${origin}/registry?tokenId=${encodeURIComponent(retirement.tokenId)}`;
      })();
      let qrDataUrl = null;

      try {
        const QR = (await import('qrcode')).default;
        qrDataUrl = await QR.toDataURL(qrTarget, {
          margin: 1,
          scale: 6,
          color: {
            dark: '#06141d',
            light: '#ffffff'
          }
        });
      } catch (e) {
        console.warn('QR generation skipped:', e.message);
      }

      const doc = new jsPDF('p', 'mm', 'a4');
      const { width: pageWidth } = getPdfPageSize(doc);
      const margin = 14;
      const usableWidth = pageWidth - margin * 2;

      drawPdfBackground(doc);
      drawPdfHeroHeader(doc, headerImageDataUrl, 44);

      let yPosition = 58;
      setPdfColor(doc, 'setTextColor', PDF_THEME.white);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(23);
      doc.text(`Certificate #${retirement.tokenId}`, margin, yPosition);
      drawPdfPill(doc, pageWidth - margin - 58, yPosition - 8.5, 58, 8.5, 'PERMANENTLY RETIRED');

      yPosition += 8.5;
      setPdfColor(doc, 'setTextColor', PDF_THEME.muted);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      doc.text('Carbon Retirement Certificate', margin, yPosition);
      doc.text(`Generated ${formatPdfDateTime(generatedAt)}`, pageWidth - margin, yPosition, { align: 'right' });

      yPosition += 13;
      drawPdfCard(doc, margin, yPosition, usableWidth, 48, {
        fill: PDF_THEME.panelAlt,
        stroke: PDF_THEME.border,
        accent: PDF_THEME.green,
        radius: 4
      });
      setPdfColor(doc, 'setTextColor', PDF_THEME.lime);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.2);
      doc.text('TOTAL CARBON BURNED', margin + 7, yPosition + 10);

      const amountFontSize = amount.length > 10 ? 27 : 33;
      setPdfColor(doc, 'setTextColor', PDF_THEME.white);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(amountFontSize);
      doc.text(amount, margin + 7, yPosition + 33);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.2);
      setPdfColor(doc, 'setTextColor', PDF_THEME.cyan);
      doc.text('for 1 tonne CO2 offset', margin + 7, yPosition + 41);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.2);
      setPdfColor(doc, 'setTextColor', PDF_THEME.muted);
      const nameLines = fitPdfLines(doc, retirement.name, 67, 2);
      doc.text(nameLines, pageWidth - margin - 72, yPosition + 17);
      setPdfColor(doc, 'setTextColor', PDF_THEME.green);
      doc.setFont('helvetica', 'bold');
      doc.text(`Issuer: ${shortenPdfValue(issuer, 24, 10)}`, pageWidth - margin - 72, yPosition + 33);
      doc.text(`Retired: ${retirementDate}`, pageWidth - margin - 72, yPosition + 39);

      yPosition += 54;
      const cardGap = 5;
      const cardWidth = (usableWidth - cardGap) / 2;
      drawPdfInfoCard(doc, margin, yPosition, cardWidth, 21, 'Retirement Date', retirementDate, { accent: PDF_THEME.green });
      drawPdfInfoCard(doc, margin + cardWidth + cardGap, yPosition, cardWidth, 21, 'Issuer', issuer, { accent: PDF_THEME.lime });
      yPosition += 24;
      drawPdfInfoCard(doc, margin, yPosition, cardWidth, 21, 'Device ID', shortenPdfValue(retirement.device, 12, 10), { mono: true, maxLines: 1, accent: PDF_THEME.cyan });
      drawPdfInfoCard(doc, margin + cardWidth + cardGap, yPosition, cardWidth, 21, 'Retired By', shortenPdfValue(retirement.retiredBy, 12, 10), { mono: true, maxLines: 1, accent: PDF_THEME.cyan });
      yPosition += 24;
      drawPdfInfoCard(
        doc,
        margin,
        yPosition,
        usableWidth,
        21,
        'Transaction Hash',
        shortenPdfValue(retirement.txHash, 18, 14),
        { mono: true, maxLines: 1, accent: PDF_THEME.green }
      );

      yPosition += 27;
      drawPdfCard(doc, margin, yPosition, usableWidth - 41, 38, {
        fill: PDF_THEME.panel,
        accent: PDF_THEME.cyan
      });
      setPdfColor(doc, 'setTextColor', PDF_THEME.cyan);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.text('VERIFICATION', margin + 5, yPosition + 8);
      setPdfColor(doc, 'setTextColor', PDF_THEME.white);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.2);
      const verifyText = 'This certificate records a permanent carbon credit retirement. The QR code links to the public registry or IPFS metadata for independent verification.';
      doc.text(doc.splitTextToSize(verifyText, usableWidth - 56), margin + 5, yPosition + 15);

      const qrSize = 27;
      const qrX = pageWidth - margin - qrSize;
      setPdfColor(doc, 'setFillColor', PDF_THEME.white);
      doc.roundedRect(qrX, yPosition + 2.5, qrSize, qrSize, 3, 3, 'F');
      if (qrDataUrl) {
        doc.addImage(qrDataUrl, 'PNG', qrX + 3, yPosition + 5.5, qrSize - 6, qrSize - 6);
      } else {
        setPdfColor(doc, 'setTextColor', PDF_THEME.ink);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.text('VERIFY', qrX + qrSize / 2, yPosition + 18, { align: 'center' });
      }
      setPdfColor(doc, 'setTextColor', PDF_THEME.white);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(5.8);
      doc.text('SCAN TO VERIFY', qrX + qrSize / 2, yPosition + 34.4, { align: 'center' });

      yPosition += 43;
      const signatureField = {
        x: margin,
        y: yPosition + 1.5,
        width: 76,
        height: 11.5
      };
      const dateField = {
        x: margin + 86,
        y: yPosition + 1.5,
        width: 58,
        height: 11.5
      };
      drawPdfFormTextField(doc, {
        fieldName: `certificate_${retirement.tokenId}_authorized_signature`,
        ...signatureField,
        fontName: 'times',
        fontStyle: 'italic',
        fontSize: 13,
        textAlign: 'center'
      });
      drawPdfFormTextField(doc, {
        fieldName: `certificate_${retirement.tokenId}_attestation_date`,
        ...dateField,
        fontName: 'helvetica',
        fontStyle: 'normal',
        fontSize: 8.5,
        maxLength: 11,
        textAlign: 'center'
      });
      setPdfColor(doc, 'setTextColor', PDF_THEME.white);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text('Authorized Registry Attestation', signatureField.x, yPosition + 22);
      doc.text('Date (DD MMM YYYY)', dateField.x, yPosition + 22);

      const sealX = pageWidth - margin - 16;
      const sealY = yPosition + 12;
      doc.setLineWidth(0.55);
      setPdfColor(doc, 'setDrawColor', PDF_THEME.green);
      doc.ellipse(sealX, sealY, 13, 13, 'S');
      doc.ellipse(sealX, sealY, 9.4, 9.4, 'S');
      setPdfColor(doc, 'setTextColor', PDF_THEME.green);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.8);
      doc.text('VERIFIED', sealX, sealY - 1, { align: 'center' });
      doc.text('RETIRED', sealX, sealY + 5, { align: 'center' });

      doc.addPage();
      drawPdfMiniHeader(doc, 'Audit Appendix', `Certificate #${retirement.tokenId}`);

      const appendixTop = 34;
      const columnGap = 6;
      const columnWidth = (usableWidth - columnGap) / 2;
      const leftColumn = margin;
      const rightColumn = margin + columnWidth + columnGap;

      const drawAuditPanel = (x, y, width, height, title, rows) => {
        drawPdfCard(doc, x, y, width, height, {
          fill: PDF_THEME.panel,
          stroke: PDF_THEME.borderSoft,
          accent: PDF_THEME.green
        });
        setPdfColor(doc, 'setTextColor', PDF_THEME.green);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.text(title.toUpperCase(), x + 4.5, y + 8);

        let rowY = y + 16;
        rows.forEach(([label, value, options = {}]) => {
          if (rowY > y + height - 7) return;
          const valueX = x + 33;
          const valueWidth = width - 38;
          const maxLines = options.maxLines || 1;
          const mono = options.mono || false;
          const lineHeight = 3.6;
          const valueLines = fitPdfLines(doc, value, valueWidth, maxLines);

          setPdfColor(doc, 'setTextColor', PDF_THEME.cyan);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(5.9);
          doc.text(toPdfText(label).toUpperCase(), x + 4.5, rowY);

          setPdfColor(doc, 'setTextColor', PDF_THEME.white);
          doc.setFont(mono ? 'courier' : 'helvetica', 'normal');
          doc.setFontSize(mono ? 5.8 : 6.3);
          doc.text(valueLines, valueX, rowY);
          rowY += Math.max(6.7, valueLines.length * lineHeight + 3);
        });
      };

      const drawBurnLotsPanel = (x, y, width, height) => {
        drawPdfCard(doc, x, y, width, height, {
          fill: PDF_THEME.panelAlt,
          stroke: PDF_THEME.borderSoft,
          accent: PDF_THEME.lime
        });
        setPdfColor(doc, 'setTextColor', PDF_THEME.lime);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.text('BURN LOTS', x + 4.5, y + 8);

        setPdfColor(doc, 'setTextColor', PDF_THEME.muted);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(5.7);
        doc.text('LOT', x + 4.5, y + 17);
        doc.text('MESSAGE ID', x + 18, y + 17);
        doc.text('AMOUNT', x + width - 4.5, y + 17, { align: 'right' });

        if (!burnLots.length) {
          setPdfColor(doc, 'setTextColor', PDF_THEME.white);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(6.3);
          doc.text('No burn plan lots recorded.', x + 4.5, y + 27);
          return;
        }

        burnLots.slice(0, 3).forEach((lot, index) => {
          const rowY = y + 27 + index * 9;
          setPdfColor(doc, 'setTextColor', PDF_THEME.white);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(6.2);
          doc.text(`#${index + 1}`, x + 4.5, rowY);
          doc.setFont('courier', 'normal');
          doc.setFontSize(5.8);
          doc.text(shortenPdfValue(lot.messageId, 10, 8), x + 18, rowY);
          doc.text(`${formatAtomicPdfAmount(lot.amount)} t`, x + width - 4.5, rowY, { align: 'right' });
        });

        if (burnLots.length > 3) {
          setPdfColor(doc, 'setTextColor', PDF_THEME.muted);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(5.7);
          doc.text(`+ ${burnLots.length - 3} additional lots`, x + 4.5, y + height - 6);
        }
      };

      drawAuditPanel(leftColumn, appendixTop, columnWidth, 78, 'Certificate Summary', [
        ['Certificate', `#${retirement.tokenId}`],
        ['Name', retirement.name, { maxLines: 2 }],
        ['Amount', `${amount} burned for 1 tonne CO2 offset`],
        ['Status', 'Permanently retired'],
        ['Issuer', issuer, { maxLines: 2 }],
        ['Retired', retirementIsoDate ? formatPdfDateTime(retirementIsoDate) : retirementDate],
        ['Generated', formatPdfDateTime(generatedAt)]
      ]);

      drawAuditPanel(leftColumn, appendixTop + 83, columnWidth, 91, 'On-Chain Proof', [
        ['Device', retirement.device || 'N/A', { mono: true, maxLines: 2 }],
        ['Retired By', retirement.retiredBy, { mono: true, maxLines: 2 }],
        ['Retire Tx', retirement.txHash || 'N/A', { mono: true, maxLines: 2 }],
        ['Mint Tx', mintTxHash || 'N/A', { mono: true, maxLines: 2 }],
        ['Message ID', mintMessageId || 'N/A', { mono: true, maxLines: 2 }],
        ['Root', provenanceRoot || 'N/A', { mono: true, maxLines: 2 }],
        ['Block', blockNumber]
      ]);

      drawAuditPanel(leftColumn, appendixTop + 179, columnWidth, 47, 'Registry Attestation', [
        [
          'Statement',
          'This document summarizes a blockchain-verifiable retirement record from the Serenity Registry. Public hashes and metadata references are provided for independent audit.',
          { maxLines: 5 }
        ]
      ]);

      drawAuditPanel(rightColumn, appendixTop, columnWidth, 78, 'Purchase & Verification', [
        ['Provider', purchaserDetails?.provider || 'N/A'],
        ['Buyer', purchaserDetails?.buyerWallet || 'N/A', { mono: true, maxLines: 2 }],
        ['Quantity', purchaserDetails?.quantity != null ? String(purchaserDetails.quantity) : 'N/A'],
        ['Unit Price', purchaserDetails?.unitPrice != null ? formatPdfMoney(purchaserDetails.unitPrice, purchaserDetails.currency || 'AUD') : 'N/A'],
        ['Total', purchaserDetails?.total != null ? formatPdfMoney(purchaserDetails.total, purchaserDetails.currency || 'AUD') : 'N/A'],
        ['Purchased', purchaserDetails?.purchasedAt ? formatPdfDateTime(purchaserDetails.purchasedAt) : 'N/A'],
        ['Session', purchaserDetails?.sessionId ? shortenPdfValue(purchaserDetails.sessionId, 14, 8) : 'N/A', { mono: true }]
      ]);

      drawBurnLotsPanel(rightColumn, appendixTop + 83, columnWidth, 61);

      drawAuditPanel(rightColumn, appendixTop + 149, columnWidth, 77, 'Metadata References', [
        ['Carbon Burned', atomicCarbonBurned ? `${formatAtomicPdfAmount(atomicCarbonBurned)}` : 'N/A'],
        ['Mint Type', mintType],
        ['Mint Time', mintTimestamp ? formatPdfDateTime(mintTimestamp) : 'N/A'],
        ['IPFS File', purchaserDetails?.metadataIpfs || 'N/A', { mono: true, maxLines: 2 }],
        ['IPFS Folder', purchaserDetails?.folderIpfs || 'N/A', { mono: true, maxLines: 2 }],
        ['Verify URL', qrTarget, { mono: true, maxLines: 2 }],
        ['Price Source', priceSource || 'N/A', { maxLines: 2 }]
      ]);

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        drawPdfFooter(doc, i, pageCount, 'This certificate is blockchain verified and immutable');
      }
      
      // Save PDF
      const fileName = `certificate-${retirement.tokenId}-details.pdf`;
      doc.save(fileName);
      
      enqueueSnackbar(`Certificate #${retirement.tokenId} exported to PDF`, { variant: 'success' });
    } catch (error) {
      console.error('Export certificate to PDF failed:', error);
      enqueueSnackbar(`Failed to export certificate: ${error.message}`, { variant: 'error' });
    }
  };

return (
  <Box sx={{ p: 3, maxWidth: '1400px', margin: '0 auto' }}>
    {/* Header */}
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'between', mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            🌍 Public Carbon Credit Registry
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Transparent, blockchain-verified carbon credit retirement records with advanced analytics
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <RealtimeStatusIndicator compact />
          <SyncButton onSync={fetchRetirements} loading={loading} />
        </Box>
      </Box>
      
      {/* Real-time update alerts */}
      {showNewDataAlert && newRetirementsCount > 0 && (
        <Alert 
          severity="success" 
          sx={{ mb: 2 }}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                size="small" 
                onClick={() => {
                  setPage(0);
                  fetchRetirements();
                  setShowNewDataAlert(false);
                  setNewRetirementsCount(0);
                }}
              >
                Refresh View
              </Button>
              <IconButton 
                size="small" 
                onClick={() => setShowNewDataAlert(false)}
              >
                <Close />
              </IconButton>
            </Box>
          }
        >
          <Box>
            <Typography variant="body2">
              🔄 {newRetirementsCount} new retirement{newRetirementsCount > 1 ? 's' : ''} received via live updates!
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Last update: {lastUpdateTime.toLocaleTimeString()}
            </Typography>
          </Box>
        </Alert>
      )}
    </Box>

    {/* Statistics Cards */}
    {stats && <StatsCards />}

    {/* Main Content Tabs */}
    <Paper sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'between', px: 2, pt: 1 }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{ borderBottom: 0 }}
        >
          <Tab 
            label="Registry Data" 
            icon={<TableView />} 
            iconPosition="start"
            sx={{ textTransform: 'none', fontSize: '1rem' }}
          />
          <Tab 
            label="Analytics & Insights" 
            icon={<Assessment />} 
            iconPosition="start"
            sx={{ textTransform: 'none', fontSize: '1rem' }}
          />
        </Tabs>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            icon={wsConnected ? <Wifi /> : <WifiOff />}
            label={wsConnected ? "Live Updates" : "Offline Mode"}
            color={wsConnected ? "success" : "default"}
            variant="outlined"
            size="small"
          />
          <Typography variant="caption" color="text.secondary">
            {stats?.lastUpdated ? `Updated: ${new Date(stats.lastUpdated).toLocaleTimeString()}` : ''}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }} />

      {/* Tab Panel 0: Registry Data */}
      {tabValue === 0 && (
        <Box>
          {/* Enhanced Search and Filter Controls */}
          <Box sx={{ p: 2 }}>
            {/* Search Mode Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={searchMode} onChange={(e, value) => setSearchMode(value)}>
                <Tab label="Simple Search" value="simple" />
                <Tab label="Advanced Search" value="advanced" />
                <Tab label="Query Builder" value="query-builder" />
              </Tabs>
            </Box>

            {/* Main Search Bar */}
            <Box sx={{ position: 'relative', mb: 2 }}>
              <TextField
                label="Global Search - Search across all certificate data..."
                placeholder="🔍 Search certificates, devices, amounts, metadata... (Ctrl+K to focus, Esc to clear)"
                variant="outlined"
                size="medium"
                fullWidth
                value={globalSearch}
                onChange={(e) => {
                  setGlobalSearch(e.target.value);
                  setSearchTerm(e.target.value);
                  setShowSuggestions(e.target.value.length > 1);
                }}
                onKeyPress={(e) => e.key === 'Enter' && handleGlobalSearch(globalSearch)}
                onFocus={() => setShowSuggestions(globalSearch.length > 1)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {globalSearch && (
                        <Tooltip title="Clear search (Esc)">
                          <IconButton size="small" onClick={() => {
                            setGlobalSearch('');
                            setSearchTerm('');
                          }}>
                            <Clear />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Search history">
                        <IconButton 
                          size="small" 
                          onClick={() => setShowSearchHistory(!showSearchHistory)}
                          color={showSearchHistory ? 'primary' : 'default'}
                        >
                          <History />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Saved searches">
                        <IconButton 
                          size="small" 
                          onClick={() => setShowSavedSearches(!showSavedSearches)}
                          color={showSavedSearches ? 'primary' : 'default'}
                        >
                          <SavedSearch />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  }
                }}
              />
              
              {/* Search Suggestions */}
              {showSuggestions && searchSuggestions.length > 0 && (
                <Paper 
                  sx={{ 
                    position: 'absolute', 
                    top: '100%', 
                    left: 0, 
                    right: 0, 
                    zIndex: 1000,
                    maxHeight: 200,
                    overflow: 'auto'
                  }}
                >
                  {searchSuggestions.map((suggestion, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 1,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        borderBottom: index < searchSuggestions.length - 1 ? 1 : 0,
                        borderColor: 'divider'
                      }}
                      onClick={suggestion.action}
                    >
                      <Typography variant="body2">{suggestion.label}</Typography>
                    </Box>
                  ))}
                </Paper>
              )}
            </Box>

            {/* Search History */}
            <Collapse in={showSearchHistory}>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'between', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <History /> Recent Searches
                  </Typography>
                  <IconButton size="small" onClick={() => setShowSearchHistory(false)}>
                    <Close />
                  </IconButton>
                </Box>
                {searchHistory.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {searchHistory.map((term, index) => (
                      <Chip
                        key={index}
                        label={term}
                        size="small"
                        onClick={() => handleGlobalSearch(term)}
                        onDelete={() => {
                          const newHistory = searchHistory.filter((_, i) => i !== index);
                          setSearchHistory(newHistory);
                          localStorage.setItem('carbonRegistry_searchHistory', JSON.stringify(newHistory));
                        }}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No recent searches
                  </Typography>
                )}
              </Paper>
            </Collapse>

            {/* Saved Searches */}
            <Collapse in={showSavedSearches}>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'between', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SavedSearch /> Saved Searches
                  </Typography>
                  <Box>
                    <Button size="small" onClick={handleSaveSearch} startIcon={<Bookmark />}>
                      Save Current
                    </Button>
                    <IconButton size="small" onClick={() => setShowSavedSearches(false)}>
                      <Close />
                    </IconButton>
                  </Box>
                </Box>
                {savedSearches.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {savedSearches.map((search) => (
                      <Box
                        key={search.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'between',
                          p: 1,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {search.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {search.term} • {new Date(search.timestamp).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Box>
                          <Button size="small" onClick={() => handleLoadSavedSearch(search)}>
                            Load
                          </Button>
                          <IconButton size="small" onClick={() => handleDeleteSavedSearch(search.id)}>
                            <Close />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No saved searches
                  </Typography>
                )}
              </Paper>
            </Collapse>

            {/* Action Buttons Row */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={() => handleGlobalSearch(globalSearch)}
                startIcon={<Search />}
                disabled={!globalSearch.trim()}
              >
                Search
              </Button>
              
              <Button
                variant="outlined"
                onClick={() => setShowFilters(!showFilters)}
                startIcon={showFilters ? <FilterAltOff /> : <FilterAlt />}
                color={Object.values(filters).some(f => f) ? 'primary' : 'inherit'}
              >
                {showFilters ? 'Hide Filters' : 'Show Filters'}
                {Object.values(filters).some(f => f) && (
                  <Badge color="primary" variant="dot" sx={{ ml: 1 }} />
                )}
              </Button>

              <Button
                variant="outlined"
                onClick={clearAllSearch}
                startIcon={<Clear />}
                disabled={!globalSearch && !Object.values(filters).some(f => f)}
              >
                Clear All
              </Button>

              <Button
                variant="outlined"
                onClick={() => {
                  if (wsConnected) {
                    requestSync();
                    setPage(0);
                    fetchRetirements();
                  } else {
                    window.location.reload();
                  }
                }}
                startIcon={wsConnected ? <Sync /> : <Refresh />}
                disabled={loading}
              >
                {wsConnected ? 'Sync' : 'Refresh'}
              </Button>
              
              <Divider orientation="vertical" flexItem />
              
              {/* Export Buttons */}
              <Button
                variant="contained"
                color="success"
                onClick={exportToCSV}
                startIcon={<TableChart />}
                disabled={loading}
              >
                Export CSV
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={exportToPDF}
                startIcon={<PictureAsPdf />}
                disabled={loading}
              >
                Export PDF
              </Button>
            </Box>

            {/* Smart Filters - Quick Access */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterAlt /> Smart Filters
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label="Recent (30 days)"
                  onClick={() => handleSmartFilter('recentRetirements')}
                  color={smartFilters.recentRetirements ? 'primary' : 'default'}
                  variant={smartFilters.recentRetirements ? 'filled' : 'outlined'}
                  size="small"
                />
                <Chip
                  label="Large (>1000 tons)"
                  onClick={() => handleSmartFilter('largeRetirements')}
                  color={smartFilters.largeRetirements ? 'primary' : 'default'}
                  variant={smartFilters.largeRetirements ? 'filled' : 'outlined'}
                  size="small"
                />
                <Chip
                  label="This Year"
                  onClick={() => handleSmartFilter('thisYear')}
                  color={smartFilters.thisYear ? 'primary' : 'default'}
                  variant={smartFilters.thisYear ? 'filled' : 'outlined'}
                  size="small"
                />
                <Chip
                  label="This Month"
                  onClick={() => handleSmartFilter('thisMonth')}
                  color={smartFilters.thisMonth ? 'primary' : 'default'}
                  variant={smartFilters.thisMonth ? 'filled' : 'outlined'}
                  size="small"
                />
                <Chip
                  label="Verified Only"
                  onClick={() => handleSmartFilter('verifiedOnly')}
                  color={smartFilters.verifiedOnly ? 'primary' : 'default'}
                  variant={smartFilters.verifiedOnly ? 'filled' : 'outlined'}
                  size="small"
                />
              </Box>
            </Box>

            {/* Query Builder Mode */}
            {searchMode === 'query-builder' && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  🔧 Advanced Query Builder
                </Typography>
                
                {queryBuilder.rules.map((rule, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                    {index > 0 && (
                      <FormControl size="small" sx={{ minWidth: 80 }}>
                        <Select
                          value={rule.logic}
                          onChange={(e) => updateQueryRule(index, 'logic', e.target.value)}
                        >
                          <MenuItem value="AND">AND</MenuItem>
                          <MenuItem value="OR">OR</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                    
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Field</InputLabel>
                      <Select
                        value={rule.field}
                        onChange={(e) => updateQueryRule(index, 'field', e.target.value)}
                        label="Field"
                      >
                        <MenuItem value="device">Device</MenuItem>
                        <MenuItem value="amount">Amount</MenuItem>
                        <MenuItem value="date">Date</MenuItem>
                        <MenuItem value="description">Description</MenuItem>
                        <MenuItem value="metadata">Metadata</MenuItem>
                        <MenuItem value="retiredBy">Retired By</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <InputLabel>Operator</InputLabel>
                      <Select
                        value={rule.operator}
                        onChange={(e) => updateQueryRule(index, 'operator', e.target.value)}
                        label="Operator"
                      >
                        <MenuItem value="contains">Contains</MenuItem>
                        <MenuItem value="equals">Equals</MenuItem>
                        <MenuItem value="gte">≥</MenuItem>
                        <MenuItem value="lte">≤</MenuItem>
                        <MenuItem value="startsWith">Starts With</MenuItem>
                        <MenuItem value="endsWith">Ends With</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <TextField
                      size="small"
                      label="Value"
                      value={rule.value}
                      onChange={(e) => updateQueryRule(index, 'value', e.target.value)}
                      sx={{ flexGrow: 1 }}
                    />
                    
                    <IconButton 
                      size="small" 
                      onClick={() => removeQueryRule(index)}
                      disabled={queryBuilder.rules.length === 1}
                    >
                      <Remove />
                    </IconButton>
                  </Box>
                ))}
                
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Button startIcon={<Add />} onClick={addQueryRule}>
                    Add Rule
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={executeQueryBuilder}
                    disabled={!queryBuilder.rules.some(r => r.field && r.operator && r.value)}
                  >
                    Execute Query
                  </Button>
                  <Button 
                    onClick={() => setQueryBuilder({
                      rules: [{ field: '', operator: '', value: '', logic: 'AND' }],
                      logic: 'AND'
                    })}
                  >
                    Clear Query
                  </Button>
                </Box>
              </Paper>
            )}

            {/* Enhanced Advanced Filters */}
            <Collapse in={showFilters}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                🔍 Advanced Filters
              </Typography>

              <Grid container spacing={2}>
                {/* Row 1 */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Device ID Filter"
                    size="small"
                    fullWidth
                    value={filters.deviceFilter}
                    onChange={(e) => handleFilterChange('deviceFilter', e.target.value)}
                    placeholder="0x..."
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Retired By Address"
                    size="small"
                    fullWidth
                    value={filters.retiredByFilter || ''}
                    onChange={(e) => handleFilterChange('retiredByFilter', e.target.value)}
                    placeholder="0x..."
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Certificate ID"
                    size="small"
                    fullWidth
                    type="number"
                    value={filters.tokenIdFilter || ''}
                    onChange={(e) => handleFilterChange('tokenIdFilter', e.target.value)}
                    placeholder="e.g., 5"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value={filters.sortBy}
                      label="Sort By"
                      onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                    >
                      <MenuItem value="timestamp">Retirement Date</MenuItem>
                      <MenuItem value="tokenId">Certificate ID</MenuItem>
                      <MenuItem value="amount">Amount</MenuItem>
                      <MenuItem value="device">Device ID</MenuItem>
                      <MenuItem value="retiredBy">Retired By</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Row 2 */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Date From"
                    type="date"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={filters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Date To"
                    type="date"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={filters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Min Amount (tons CO₂)"
                    type="number"
                    size="small"
                    fullWidth
                    value={filters.minAmount}
                    onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                    placeholder="0"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Max Amount (tons CO₂)"
                    type="number"
                    size="small"
                    fullWidth
                    value={filters.maxAmount}
                    onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                    placeholder="1000"
                  />
                </Grid>

                {/* Row 3 - Filter Actions */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1 }}>
                    <FormControl size="small">
                      <InputLabel>Sort Order</InputLabel>
                      <Select
                        value={filters.sortOrder}
                        label="Sort Order"
                        onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                        sx={{ minWidth: 120 }}
                      >
                        <MenuItem value="desc">Newest First</MenuItem>
                        <MenuItem value="asc">Oldest First</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <Button
                      variant="contained"
                      onClick={() => {
                        setPage(0);
                        fetchRetirements();
                      }}
                      startIcon={<Search />}
                    >
                      Apply Filters
                    </Button>
                    
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setFilters({
                          deviceFilter: '',
                          dateFrom: '',
                          dateTo: '',
                          minAmount: '',
                          maxAmount: '',
                          retiredByFilter: '',
                          tokenIdFilter: '',
                          sortBy: 'timestamp',
                          sortOrder: 'desc'
                        });
                        setSearchTerm('');
                        setPage(0);
                      }}
                      startIcon={<Refresh />}
                    >
                      Clear All Filters
                    </Button>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                      {totalRecords} records found
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Collapse> {/* ✅ properly close Collapse */}
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Search Results Summary */}
          {(globalSearch || Object.values(filters).some(f => f)) && (
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Search />
                  <Box>
                    <Typography variant="h6">
                      Search Results: {totalRecords.toLocaleString()} certificates found
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {globalSearch && `Searching for: "${globalSearch}"`}
                      {globalSearch && Object.values(filters).some(f => f) && ' • '}
                      {Object.values(filters).some(f => f) && 'Additional filters applied'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {Object.values(smartFilters).some(f => f) && (
                    <Chip 
                      label="Smart Filters Active" 
                      size="small" 
                      color="warning" 
                      variant="outlined"
                    />
                  )}
                  <Button 
                    size="small" 
                    onClick={clearAllSearch}
                    startIcon={<Clear />}
                    sx={{ color: 'inherit' }}
                  >
                    Clear All
                  </Button>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Data Table */}
          <Box sx={{ mt: 2 }}>
            <Paper>
              {/* Table Header with Real-time Status */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'between' }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TableView />
                  Registry Data
                  {wsConnected && (
                    <Chip 
                      icon={<RadioButtonChecked />} 
                      label="Live" 
                      color="success" 
                      size="small" 
                      variant="outlined"
                    />
                  )}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {totalRecords.toLocaleString()} total records
                  </Typography>
                  {wsConnected ? (
                    <Typography variant="caption" color="success.main">
                      🔄 Real-time sync active
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="warning.main">
                      📡 Manual refresh mode
                    </Typography>
                  )}
                </Box>
              </Box>
              
              <TableContainer data-testid="registry-table">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={filters.sortBy === 'tokenId'}
                          direction={filters.sortOrder}
                          onClick={() => handleSort('tokenId')}
                        >
                          Certificate ID
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={filters.sortBy === 'timestamp'}
                          direction={filters.sortOrder}
                          onClick={() => handleSort('timestamp')}
                        >
                          Retirement Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={filters.sortBy === 'amount'}
                          direction={filters.sortOrder}
                          onClick={() => handleSort('amount')}
                        >
                          Amount (tokens)
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Device ID</TableCell>
                      <TableCell>Retired By</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : retirements.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No retirement records found
                        </TableCell>
                      </TableRow>
                    ) : (
                      retirements.map((retirement, index) => {
                        // Check if this is a recent retirement (first few items and recent timestamp)
                        const isRecent = index < 3 && wsConnected && 
                          retirement.timestamp && 
                          (Date.now() / 1000) - retirement.timestamp < 300; // 5 minutes
                        
                        return (
                          <TableRow 
                            key={retirement.tokenId} 
                            hover
                            sx={{
                              ...(isRecent && {
                                bgcolor: 'success.light',
                                opacity: 0.9,
                                animation: 'pulse 2s ease-in-out',
                                '@keyframes pulse': {
                                  '0%': { bgcolor: 'success.light' },
                                  '50%': { bgcolor: 'success.main' },
                                  '100%': { bgcolor: 'success.light' }
                                }
                              })
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" fontWeight="bold">
                                  #<HighlightText text={retirement.tokenId} highlight={globalSearch} />
                                </Typography>
                                {isRecent && (
                                  <Chip 
                                    label="NEW" 
                                    color="success" 
                                    size="small" 
                                    variant="filled"
                                    sx={{ fontSize: '0.6rem', height: '16px' }}
                                  />
                                )}
                              </Box>
                            </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              <HighlightText text={formatDate(retirement.timestamp)} highlight={globalSearch} />
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              <HighlightText text={formatAmount(retirement.amountFormatted)} highlight={globalSearch} />
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              <HighlightText 
                                text={retirement.device ? formatAddress(retirement.device) : 'N/A'} 
                                highlight={globalSearch} 
                              />
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              <HighlightText text={formatAddress(retirement.retiredBy)} highlight={globalSearch} />
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label="RETIRED"
                              color="error"
                              size="small"
                              icon={<Verified />}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Button
                                size="small"
                                onClick={() => handleViewDetails(retirement)}
                                startIcon={<Visibility />}
                              >
                                View
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                onClick={() => handleOpenNftAudit(retirement)}
                                startIcon={<Gavel />}
                              >
                                NFT Audit
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={totalRecords}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Paper>
          </Box>
        </Box> // ✅ close Box wrapping Tab Panel 0
      )}

      {/* Tab Panel 1: Analytics & Insights */}
      {tabValue === 1 && (
        <Box sx={{ p: 2 }}>
          <AnalyticsCharts />
        </Box>
      )}
    </Paper>

    {/* Enhanced Certificate Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Verified color="success" />
            <Typography variant="h6">
              Certificate #{<HighlightText text={selectedRetirement?.tokenId} highlight={globalSearch} />} - PERMANENTLY RETIRED
            </Typography>
            <Chip label="VERIFIED" color="success" size="small" />
            {/* Purchased indicator */}
            {(isPurchased?.(selectedRetirement?.tokenId) || !!purchaserDetails) && (
              <Chip label="PURCHASED" color="info" size="small" />
            )}
            {globalSearch && globalSearch.trim() && (
              <Chip 
                label={`🔍 Highlighting: "${globalSearch}"`} 
                color="warning" 
                size="small" 
                variant="outlined"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRetirement && (
            <Box>
              {/* Search Context Panel */}
              {globalSearch && globalSearch.trim() && (
                <Alert 
                  severity="info" 
                  sx={{ mb: 3 }}
                  action={
                    <Button 
                      size="small" 
                      onClick={() => {
                        setGlobalSearch('');
                        setSearchTerm('');
                      }}
                    >
                      Clear Highlights
                    </Button>
                  }
                >
                  <Box>
                    <Typography variant="body2">
                      <strong>Search highlights active:</strong> Found{' '}
                      <Chip 
                        label={`${countSearchMatches(selectedRetirement, globalSearch)} matches`}
                        size="small"
                        color="warning"
                        sx={{ mx: 1 }}
                      />
                      for "{globalSearch}" in this certificate.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      💡 Tip: Use the search bar above to find certificates, then view details to see highlighted matches.
                    </Typography>
                  </Box>
                </Alert>
              )}

              {/* Certificate Header */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
                <Typography variant="h6" gutterBottom>
                  <HighlightText text={selectedRetirement.name} highlight={globalSearch} />
                </Typography>
                <Typography variant="body2">
                  <HighlightText text={selectedRetirement.description} highlight={globalSearch} />
                </Typography>
              </Paper>

              {/* Main Details Grid */}
              <Grid container spacing={3}>
                {/* Left Column */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom color="primary">
                      Certificate Information
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Certificate ID</Typography>
                        <Typography variant="body1" fontWeight="bold">
                          #{<HighlightText text={selectedRetirement.tokenId} highlight={globalSearch} />}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Carbon Tokens Burned</Typography>
                        <Typography variant="h5" color="success.main" fontWeight="bold">
                          <HighlightText text={
                              selectedRetirement.amount
                                //? (Number(selectedRetirement.amount) / 1e18).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                ? (Number(selectedRetirement.amount) ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                : "0.00"
                            } highlight={globalSearch} />  
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Retirement Date</Typography>
                        <Typography variant="body1">
                          <HighlightText text={formatDate(selectedRetirement.timestamp)} highlight={globalSearch} />
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                        <Chip label="PERMANENTLY RETIRED" color="error" icon={<Verified />} />
                      </Box>
                    </Box>
                  </Card>
                </Grid>

                {/* Right Column */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom color="primary">
                      Blockchain Details
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Transaction Hash</Typography>
                        <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                          <HighlightText text={selectedRetirement.txHash || 'N/A'} highlight={globalSearch} />
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Device ID</Typography>
                        <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                          <HighlightText text={selectedRetirement.device || 'N/A'} highlight={globalSearch} />
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Retired By</Typography>
                        <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                          <HighlightText text={selectedRetirement.retiredBy} highlight={globalSearch} />
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Issuer</Typography>
                        <Typography variant="body2">
                          <HighlightText 
                            text={selectedRetirement.metadata?.attributes?.find(a => a.trait_type === "Issuer")?.value || 'N/A'} 
                            highlight={globalSearch} 
                          />
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>

                {/* Full Width Metadata Section */}
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom color="primary">
                      Certificate Attributes
                    </Typography>
                    {selectedRetirement.metadata?.attributes && (
                      <Grid container spacing={2}>
                        {selectedRetirement.metadata.attributes.map((attr, index) => (
                          <Grid item xs={12} sm={6} md={4} key={index}>
                            <Box sx={{ p: 1, bgcolor: 'background.paper', borderRadius: 1, border: 1, borderColor: 'divider' }}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                <HighlightText text={attr.trait_type} highlight={globalSearch} />
                              </Typography>
                              <Typography variant="body2" fontWeight="medium">
                                <HighlightText text={attr.value} highlight={globalSearch} />
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Card>
                </Grid>

                {/* Verification Section */}
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ p: 2, bgcolor: 'background.paper', color: 'text.secondary' }}>
                    <Typography variant="h6" gutterBottom>
                      🔒 Verification & Authenticity
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Verified color="inherit" />
                          <Typography variant="body2">Blockchain Verified</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Public color="inherit" />
                          <Typography variant="body2">Publicly Auditable</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Timeline color="inherit" />
                          <Typography variant="body2">Immutable Record</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>

                {/* Purchaser Details Section (visible when available) */}
                {(loadingPurchaseInfo || purchaserDetails) && (
                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="h6" color="primary">
                          Purchaser Details
                        </Typography>
                        {loadingPurchaseInfo && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={18} />
                            <Typography variant="caption" color="text.secondary">Loading…</Typography>
                          </Box>
                        )}
                      </Box>
                      {purchaserDetails ? (
                        <Grid container spacing={2}>
                          {/* Helper to build IPFS metadata URLs (ipfs:// and HTTP gateway) */}
                          {(() => {
                            return null; // placeholder to keep JSX structure consistent
                          })()}
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="text.secondary">Provider</Typography>
                            <Typography variant="body2" fontWeight="medium">{purchaserDetails.provider || '—'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="text.secondary">Buyer Wallet</Typography>
                            <Typography variant="body2" fontFamily="monospace">
                              {purchaserDetails.buyerWallet ? `${purchaserDetails.buyerWallet.slice(0, 6)}...${purchaserDetails.buyerWallet.slice(-4)}` : '—'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={2}>
                            <Typography variant="caption" color="text.secondary">Quantity</Typography>
                            <Typography variant="body2" fontWeight="medium">{purchaserDetails.quantity ?? '—'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={2}>
                            <Typography variant="caption" color="text.secondary">Unit Price</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {purchaserDetails.unitPrice != null ? `${purchaserDetails.currency || 'AUD'} ${Number(purchaserDetails.unitPrice).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '—'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={2}>
                            <Typography variant="caption" color="text.secondary">Total</Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {purchaserDetails.total != null ? `${purchaserDetails.currency || 'AUD'} ${Number(purchaserDetails.total).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '—'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="text.secondary">Status</Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {purchaserDetails.status || 'unknown'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={5}>
                            <Typography variant="caption" color="text.secondary">Session ID</Typography>
                            <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>{purchaserDetails.sessionId || '—'}</Typography>
                          </Grid>
                          <Grid item xs={12} sm={6} md={4}>
                            <Typography variant="caption" color="text.secondary">Purchased At</Typography>
                            <Typography variant="body2">
                              {purchaserDetails.purchasedAt ? new Date(purchaserDetails.purchasedAt).toLocaleString() : '—'}
                            </Typography>
                          </Grid>
                          {/* IPFS Links (Metadata) */}
                          <Grid item xs={12} sm={6} md={6}>
                            <Typography variant="caption" color="text.secondary">Metadata (IPFS)</Typography>
                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                              {(() => {
                                // Build ipfs:// path for metadata.json using robust rules
                                const toPath = (value) => (value || '').replace(/^ipfs:\/\//, '');
                                const gwBase = (process.env.REACT_APP_IPFS_GATEWAY || 'https://serenity.ing/ipfs/').replace(/\/?$/, '/');
                                let path = null;
                                if (purchaserDetails?.metadataIpfs) {
                                  // metadataIpfs can be a bare CID or path
                                  path = toPath(purchaserDetails.metadataIpfs);
                                } else if (purchaserDetails?.folderIpfs) {
                                  const folder = toPath(purchaserDetails.folderIpfs).replace(/\/$/, '');
                                  path = `${folder}/metadata.json`;
                                } else {
                                  // try trait in on-chain metadata
                                  const trait = selectedRetirement?.metadata?.attributes?.find(a => (a?.trait_type || '').toLowerCase() === 'metadata_ipfs')?.value;
                                  if (trait) path = toPath(trait);
                                }
                                if (!path) return '—';
                                const ipfsUri = `ipfs://${path}`;
                                const httpUrl = gwBase + path;
                                return (
                                  <>
                                    {ipfsUri}
                                    {' '}·{' '}
                                    <a href={httpUrl} target="_blank" rel="noopener noreferrer">Open</a>
                                  </>
                                );
                              })()}
                            </Typography>
                          </Grid>
                          {/* Optional: also render HTTP URL explicitly if needed in future */}
                        </Grid>
                      ) : (
                        !loadingPurchaseInfo && (
                          <Typography variant="body2" color="text.secondary">
                            No purchase recorded for this certificate.
                          </Typography>
                        )
                      )}
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>

          {/* Export Buttons */}
          <Button
            variant="outlined"
            color="success"
            onClick={() => exportCertificateToCSV(selectedRetirement)}
            startIcon={<TableChart />}
            size="large"
          >
            Export CSV
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => exportCertificateToPDF(selectedRetirement)}
            startIcon={<PictureAsPdf />}
            size="large"
          >
            Export PDF
          </Button>
          {/* Purchase Button - hidden if already purchased */}
          {!isPurchased(selectedRetirement?.tokenId) && (
            <Button
              variant="contained"
              color="info"
              onClick={async () => {
                if (!selectedRetirement) return;
                try {
                  enqueueSnackbar('Initializing checkout…', { variant: 'info' });
                  const buyerAddress = await resolveConnectedWalletAddress();
                  await purchaseCarbonCredit({
                    retirement: selectedRetirement,
                    quantity: 1,
                    buyerAddress,
                  });
                  enqueueSnackbar('Redirecting to payment provider…', { variant: 'success' });
                } catch (e) {
                  enqueueSnackbar(`Payment initialization failed: ${e.message}`, { variant: 'error' });
                }
              }}
              startIcon={<ShoppingCartIcon />}
              size="large"
            >
              Purchase
            </Button>
          )}
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => selectedRetirement && handleOpenNftAudit(selectedRetirement)}
            startIcon={<Gavel />}
            size="large"
            disabled={!selectedRetirement}
          >
            Open NFT Audit
          </Button>
          {selectedRetirement?.txHash && (
            <Button
              variant="contained"
              onClick={() =>
                window.open(`${EXPLORER_TX_BASE}/${selectedRetirement.txHash}`, '_blank', 'noopener,noreferrer')
              }
              startIcon={<Public />}
              size="large"
            >
              View on Blockchain
            </Button>
          )}
          
          
          {/* Close button moved to the right */}
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setDialogOpen(false)} size="large">
            Close
          </Button>
        </DialogActions>
      </Dialog>
  </Box>
);


}

export default RegistryHome;
